import { storage } from './shell/storage.js';
import { WebShellBridge, installBridge } from './shell/bridge.js';
import { showToast } from './shell/toast.js';
import { syncManager } from './sync/syncManager.js';
import { syncState, type SyncStatus } from './sync/syncState.js';
import {
  REMOTE_ROOT_PREFIX, REMOTE_MODULES_PREFIX, REMOTE_STORAGE_PREFIX,
  type RemoteFile,
} from './sync/types.js';
import type { SyncProvider } from './sync/syncProvider.js';
import { getOrCreateDeviceId } from './shell/deviceId.js';
import {
  listCheckpoints, readCheckpoint, type Checkpoint, type CheckpointMeta,
  getCheckpointKeep, setCheckpointKeep, isCheckpointingEnabled, setCheckpointingEnabled,
  restoreModuleFromCheckpoint, restoreWholeCheckpoint,
} from './sync/checkpoints.js';
import { scanStaleFiles, sweepFiles, type SweepScan } from './sync/sweep.js';
import { isGoogleSyncConfigured } from './config.js';
import { registerServiceWorker } from './pwa/swClient.js';
import { installPrompt } from './pwa/installPrompt.js';
import type { ModuleRecord } from './shell/types.js';
import { getAiProfiles, setAiProfile, deleteAiProfile, type AiProfile } from './ai/aiProfiles.js';
import { AI_PROVIDERS, fetchModelsForProvider, OLLAMA_DEFAULT_BASE_URL } from './ai/modelDiscovery.js';
import { THEME_PRESETS, loadTheme, saveTheme, applyTheme, buildTokenCss, isModuleThemingEnabled, setModuleThemingEnabled, type ThemePreset } from './shell/theme.js';
import { rehydrateNotifications } from './shell/notifications.js';
import {
  initAutoSync, manualSync,
  isAutoSyncEnabled, setAutoSyncEnabled,
  getAutoSyncIntervalMinutes, setAutoSyncIntervalMinutes,
  refreshAutoSyncCadence,
} from './sync/autoSync.js';
import { getSharedBindings, bindSharedModule, unbindSharedModule } from './sync/sharedSync.js';

/**
 * Launcher entry — the shell's home screen. Mirrors the Android launcher:
 * a grid of installed modules, tap to open, plus a sync status pill and a
 * Settings overlay for Cloud Sync + per-module opt-in.
 */

const LAUNCHER_ID = '__launcher__';
const BUILTIN_IDS = ['hello', 'promptgenerator', 'journeystudio', 'gameroom'];
/** Bump when bundled module code changes so existing installs re-seed it. */
const SEED_VERSION = 12;
const SEED_VERSION_KEY = 'shell_seed_version';

/**
 * Owner-only modules — hidden from the launcher tiles + sidebar unless owner
 * mode is on (mirrors Android's OWNER_MODULE_IDS). Owner mode unlocks via a
 * 7-tap on the "Shell" title. They stay seeded/reachable by URL regardless.
 */
const OWNER_MODULE_IDS = ['journeystudio'];
const OWNER_FLAG_KEY = 'shell.owner';
function ownerMode(): boolean { return localStorage.getItem(OWNER_FLAG_KEY) === '1'; }
function setOwnerMode(on: boolean): void {
  if (on) localStorage.setItem(OWNER_FLAG_KEY, '1');
  else localStorage.removeItem(OWNER_FLAG_KEY);
}

/**
 * 7-tap on the "Shell" title toggles owner mode (mirrors the Android
 * version-row gesture). Reveals owner-only tiles like the Journey Studio.
 */
let ownerTaps = 0;
let ownerTapTimer: ReturnType<typeof setTimeout> | undefined;
function wireOwnerUnlock(titleEl: Element | null): void {
  if (!titleEl) return;
  titleEl.addEventListener('click', () => {
    clearTimeout(ownerTapTimer);
    ownerTaps += 1;
    if (!ownerMode() && ownerTaps >= 4 && ownerTaps < 7) showToast(`${7 - ownerTaps} more…`);
    if (ownerTaps >= 7) {
      ownerTaps = 0;
      const next = !ownerMode();
      setOwnerMode(next);
      showToast(next ? '🧭 Owner mode on — Journey Studio revealed' : 'Owner mode off');
      render();
      return;
    }
    ownerTapTimer = setTimeout(() => { ownerTaps = 0; }, 1200);
  });
}

async function bootstrap(): Promise<void> {
  // Theme first — before any rendering, so there's no colour flash.
  applyTheme(loadTheme());

  await storage.hydrate();
  await seedBuiltins();

  installBridge(new WebShellBridge(LAUNCHER_ID));

  // Bridge module-iframe → host postMessage handlers.
  window.addEventListener('message', (ev) => {
    if (ev.origin !== window.location.origin) return;
    // Invisible warm-up frames stay silent — no toasts or navigation.
    if (ev.source && warmupWindows.has(ev.source as Window)) return;
    const data = ev.data;
    if (!data || data.__shell !== true) return;
    if (data.type === 'toast' && typeof data.msg === 'string') showToast(data.msg);
    // Embedded module called openModule — switch the pane.
    if (data.type === 'openModule' && typeof data.id === 'string') {
      if (data.id === 'launcher') setOpenModule(null);
      else if (storage.getModule(data.id)) setOpenModule(data.id);
      else showToast(`Module "${data.id}" not found`);
    }
  });

  // Browser back/forward moves through the #m=<id> pane history.
  window.addEventListener('popstate', () => render());

  // Crossing the desktop/mobile breakpoint: re-render; if a module was
  // open in the pane and we're now phone-sized, hand off to the
  // full-page flow so the user keeps their place.
  desktopMql.addEventListener('change', () => {
    const openId = openModuleIdFromHash();
    if (!desktopMql.matches && openId) {
      const url = new URL('module.html', window.location.href);
      url.searchParams.set('id', openId);
      window.location.href = url.toString();
      return;
    }
    render();
  });

  // Rehydrate sync provider from preferences + attempt silent re-auth.
  await syncManager.initFromStorage();

  // Subscribe to sync state so the pill updates live.
  syncState.subscribe(renderSyncPill);

  // PWA: register service worker + wire the install prompt.
  installPrompt.init();
  installPrompt.subscribe(renderInstallButton);
  void registerServiceWorker(showUpdateBanner);

  // Restore any pending module-scheduled notifications across page reloads.
  rehydrateNotifications();

  // Background sync on launch, visibility change, and an interval.
  initAutoSync();

  render();

  // Warm up donor modules so their published peek() data is fresh.
  scheduleBackgroundWarmup();
}

// ─── Background warm-up (web's equivalent of Android's invisible load) ──────

/** contentWindows of active warm-up frames — their messages are ignored. */
const warmupWindows = new Set<Window>();

/**
 * A few seconds after boot, load every backgroundRefresh-flagged module in
 * a hidden iframe (staggered) so it runs its init and republishes its data,
 * then tear the frame down. Mirrors MainActivity.scheduleBackgroundWarmup().
 */
function scheduleBackgroundWarmup(): void {
  const flagged = (storage.listModules() as Array<[string, ModuleRecord]>)
    .filter(([, rec]) => rec.backgroundRefresh);
  if (!flagged.length) return;
  window.setTimeout(() => {
    flagged.forEach(([id], i) => window.setTimeout(() => warmUpModule(id), i * 500));
  }, 3000);
}

function warmUpModule(id: string): void {
  const url = new URL('module.html', window.location.href);
  url.searchParams.set('id', id);
  url.searchParams.set('bg', '1');
  const frame = document.createElement('iframe');
  frame.src = url.toString();
  frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  document.body.appendChild(frame);
  if (frame.contentWindow) warmupWindows.add(frame.contentWindow);
  // 10s is plenty for init + publish; then free the resources.
  window.setTimeout(() => {
    if (frame.contentWindow) warmupWindows.delete(frame.contentWindow);
    frame.remove();
  }, 10_000);
}

/**
 * Shown when a newer SW has installed and is waiting. Click → activate it
 * (which fires `controllerchange` in swClient, triggering a reload).
 */
function showUpdateBanner(applyUpdate: () => void): void {
  if (document.getElementById('update-banner')) return;
  const banner = document.createElement('div');
  banner.id = 'update-banner';
  banner.className = 'update-banner';
  banner.innerHTML = `
    <span>🎉 A new version of Shell is available.</span>
    <button class="banner-btn" id="update-apply">Reload</button>
    <button class="banner-close" id="update-dismiss" aria-label="Dismiss">✕</button>
  `;
  document.body.appendChild(banner);
  document.getElementById('update-apply')!.addEventListener('click', applyUpdate);
  document.getElementById('update-dismiss')!.addEventListener('click', () => banner.remove());
}

/**
 * Toggles the "Install app" button in the settings overlay when Chrome's
 * beforeinstallprompt fires. The button is rendered inside Settings, so
 * the visibility is driven by the Settings code re-reading state on open.
 * Here we also surface a one-time toast the first time install is offered.
 */
let installToastShown = false;
function renderInstallButton(state: { available: boolean; installed: boolean }): void {
  if (state.available && !installToastShown) {
    installToastShown = true;
    showToast('💡 Install Shell as an app — find it in ⚙️ Settings');
  }
}

/**
 * Installs the bundled modules on first run, and refreshes their CODE when
 * SEED_VERSION is bumped (the web equivalent of Android shipping new builtin
 * assets in an app update). User data, sync modes, and extra files such as
 * revisions are preserved on refresh.
 */
async function seedBuiltins(): Promise<void> {
  const fresh = storage.listModules().length === 0;
  const seeded = Number(localStorage.getItem(SEED_VERSION_KEY) || '0');
  if (!fresh && seeded >= SEED_VERSION) return;

  for (const id of BUILTIN_IDS) {
    try {
      const res = await fetch(`builtin/${id}/index.html`);
      if (!res.ok) continue;
      const html = await res.text();
      const existing = storage.getModule(id) as ModuleRecord | undefined;
      if (existing) {
        if (existing.htmlSource === html) continue;
        storage.putModule(id, {
          ...existing,
          htmlSource: html,
          files: { ...existing.files, 'index.html': html },
          lastModifiedMs: Date.now(),
        });
      } else {
        storage.putModule(id, {
          id, name: prettyName(id), htmlSource: html,
          files: { 'index.html': html },
          lastModifiedMs: Date.now(),
          lastDataSyncMs: 0,
          dataSyncMode: 'local_only',
          codeSyncMode: 'local_only',
        } satisfies ModuleRecord);
      }
    } catch (err) {
      console.warn(`[launcher] failed to seed builtin ${id}`, err);
    }
  }
  localStorage.setItem(SEED_VERSION_KEY, String(SEED_VERSION));
}

function prettyName(id: string): string {
  const names: Record<string, string> = { promptgenerator: 'Module Builder', hello: 'Hello', journeystudio: 'Journey Studio', gameroom: 'Game Room' };
  return names[id] ?? id.charAt(0).toUpperCase() + id.slice(1);
}

function tileEmoji(id: string): string {
  const icons: Record<string, string> = { promptgenerator: '✨', hello: '👋', journeystudio: '🧭', gameroom: '🎮' };
  return icons[id] ?? '🧩';
}

// ─── Rendering ───────────────────────────────────────────────────────────────

/**
 * ≥900px gets the desktop split: a compact sidebar (launcher) on the left,
 * a module pane on the right. Below that, the phone layout — full-page
 * navigation to module.html — stays as-is.
 */
const desktopMql = window.matchMedia('(min-width: 900px)');
function isDesktop(): boolean { return desktopMql.matches; }

/** The module open in the desktop pane, driven by the #m=<id> hash. */
function openModuleIdFromHash(): string | null {
  const m = window.location.hash.match(/^#m=([a-z0-9_-]+)$/);
  return m ? m[1] : null;
}

function setOpenModule(id: string | null): void {
  const hash = id ? `#m=${id}` : '';
  if (window.location.hash !== hash) {
    history.pushState(null, '', hash || window.location.pathname + window.location.search);
  }
  render();
}

function render(): void {
  const root = document.getElementById('app');
  if (!root) return;
  document.body.classList.toggle('edit-mode', editMode);
  root.innerHTML = '';
  if (isDesktop()) renderDesktop(root);
  else renderMobile(root);
  renderSyncPill(syncState.snapshot);
}

// ─── Mobile layout (phone-style, full-page modules) ─────────────────────────

function renderMobile(root: HTMLElement): void {
  const header = document.createElement('header');
  header.className = 'launcher-header';
  header.innerHTML = `
    <h1>Shell</h1>
    <div class="header-actions">
      <button class="sync-pill" id="sync-pill" title="Tap to sync now">
        <span class="sync-dot"></span><span id="sync-text">local</span>
      </button>
      <button class="icon-btn" id="edit-btn" title="Edit layout" aria-pressed="${editMode ? 'true' : 'false'}">${editMode ? '✓' : '✏️'}</button>
      <button class="icon-btn" id="settings-btn" title="Settings">⚙️</button>
    </div>
  `;
  root.appendChild(header);
  wireOwnerUnlock(header.querySelector('h1'));

  const wrap = document.createElement('div');
  wrap.className = 'sections-wrap';
  root.appendChild(wrap);
  renderSections(wrap);

  // Floating ➕ — the v3 add-module entry point.
  const fab = document.createElement('button');
  fab.id = 'add-fab';
  fab.title = 'Add a module';
  fab.textContent = '+';
  fab.addEventListener('click', showAddSheet);
  root.appendChild(fab);

  if (editMode) root.appendChild(buildEditBanner());

  document.getElementById('edit-btn')?.addEventListener('click', toggleEditMode);
  document.getElementById('settings-btn')?.addEventListener('click', showSettings);
  document.getElementById('sync-pill')?.addEventListener('click', () => {
    void manualSync();
  });
}

// ─── Sections (mirrors Android sections_config.json + module.json `section`) ──

const SECTIONS_KEY = 'shell.sections';
const DEFAULT_SECTION = 'General';

/** Edit mode shows wiggle, section controls, and the Done banner. */
let editMode = false;

function loadSectionList(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? 'null');
    if (Array.isArray(arr)) return arr.filter((s): s is string => typeof s === 'string');
  } catch { /* fall through to empty */ }
  return [];
}
function saveSectionList(list: string[]): void {
  localStorage.setItem(SECTIONS_KEY, JSON.stringify(list));
}

/** A module's section lives in its module.json, so it rides code sync. */
function moduleSection(rec: ModuleRecord): string {
  try {
    const mj = JSON.parse(rec.files?.['module.json'] ?? '{}') as { section?: string };
    if (typeof mj.section === 'string' && mj.section.trim()) return mj.section.trim();
  } catch { /* default below */ }
  return DEFAULT_SECTION;
}

/** Writes a module's section into its record + module.json and bumps mtime. */
function setModuleSection(id: string, section: string): void {
  const cur = storage.getModule(id) as ModuleRecord | undefined;
  if (!cur) return;
  let label = cur.name;
  let icon = cur.icon ?? '🧩';
  try {
    const mj = JSON.parse(cur.files?.['module.json'] ?? '{}') as { label?: string; icon?: string };
    if (typeof mj.label === 'string') label = mj.label;
    if (typeof mj.icon === 'string') icon = mj.icon;
  } catch { /* use record values */ }
  storage.putModule(id, {
    ...cur,
    files: {
      ...cur.files,
      'index.html': cur.htmlSource,
      'module.json': JSON.stringify({ label, icon, section }),
    },
    // Bump so code sync pushes the change (whole-module LWW).
    lastModifiedMs: Date.now(),
  });
}

/** Saved order first, then any section a module references; default ensured. */
function effectiveSections(): string[] {
  const out = [...loadSectionList()];
  const ensure = (s: string): void => { if (!out.includes(s)) out.push(s); };
  for (const [, rec] of storage.listModules() as Array<[string, ModuleRecord]>) {
    ensure(moduleSection(rec));
  }
  if (!out.length) out.push(DEFAULT_SECTION);
  return out;
}

function modulesBySection(): Map<string, Array<[string, ModuleRecord]>> {
  const groups = new Map<string, Array<[string, ModuleRecord]>>();
  for (const s of effectiveSections()) groups.set(s, []);
  const showOwner = ownerMode();
  for (const [id, rec] of storage.listModules() as Array<[string, ModuleRecord]>) {
    if (!showOwner && OWNER_MODULE_IDS.includes(id)) continue;   // owner-only tiles hidden
    const s = moduleSection(rec);
    if (!groups.has(s)) groups.set(s, []);
    groups.get(s)!.push([id, rec]);
  }
  // Apply the saved drag order within each section; unranked modules (never
  // dragged) keep their listModules() order, trailing the ranked ones.
  const rank = new Map(loadModuleOrder().map((id, i) => [id, i] as const));
  const rankOf = (id: string): number => (rank.has(id) ? rank.get(id)! : Number.MAX_SAFE_INTEGER);
  for (const list of groups.values()) {
    list.sort((a, b) => rankOf(a[0]) - rankOf(b[0]));
  }
  return groups;
}

// ─── Drag ordering persistence (web equivalent of Android reorderModule) ──────

const MODULE_ORDER_KEY = 'shell.moduleOrder';
function loadModuleOrder(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(MODULE_ORDER_KEY) ?? 'null');
    if (Array.isArray(arr)) return arr.filter((s): s is string => typeof s === 'string');
  } catch { /* fall through */ }
  return [];
}
function saveModuleOrder(ids: string[]): void {
  localStorage.setItem(MODULE_ORDER_KEY, JSON.stringify(ids));
}

// ─── Section mutations ───────────────────────────────────────────────────────

function addSectionNamed(name: string): void {
  const n = name.trim();
  if (!n) return;
  const list = effectiveSections();
  if (list.includes(n)) { showToast('That section already exists'); return; }
  saveSectionList([...list, n]);
}
function renameSectionNamed(oldName: string, newName: string): void {
  const n = newName.trim();
  if (!n || n === oldName) return;
  const list = effectiveSections();
  if (list.includes(n)) { showToast('A section with that name exists'); return; }
  saveSectionList(list.map((s) => (s === oldName ? n : s)));
  for (const [id, rec] of storage.listModules() as Array<[string, ModuleRecord]>) {
    if (moduleSection(rec) === oldName) setModuleSection(id, n);
  }
}
function deleteSectionNamed(name: string): void {
  const remaining = effectiveSections().filter((s) => s !== name);
  saveSectionList(remaining.length ? remaining : [DEFAULT_SECTION]);
  const fallback = remaining[0] ?? DEFAULT_SECTION;
  for (const [id, rec] of storage.listModules() as Array<[string, ModuleRecord]>) {
    if (moduleSection(rec) === name) setModuleSection(id, fallback);
  }
}
function moveSection(name: string, dir: -1 | 1): void {
  const list = effectiveSections();
  const i = list.indexOf(name);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
  saveSectionList(list);
}

// ─── Tile + sectioned grid rendering ─────────────────────────────────────────

/** Builds one launcher tile. A div, not a <button> — the ⋮ inside is a real
 *  button, and nested interactive elements are invalid HTML. */
function buildTile(id: string, rec: ModuleRecord): HTMLElement {
  const tile = document.createElement('div');
  tile.className = 'tile';
  tile.dataset.moduleId = id;
  tile.setAttribute('role', 'button');
  tile.tabIndex = 0;
  tile.innerHTML = `
    <div class="tile-icon">${escapeHtml(rec.icon ?? tileEmoji(id))}</div>
    <div class="tile-name">${escapeHtml(rec.name)}</div>
    <button class="tile-dots" title="Module options" aria-label="Options for ${escapeHtml(rec.name)}">
      <span class="dot-circle">⋮</span>
    </button>
  `;
  // In edit mode a plain tap opens the module's sheet (where "Move to section"
  // lives); otherwise it opens the module.
  const primary = (): void => { if (editMode) showModuleMenu(id, rec); else openModule(id); };
  tile.addEventListener('click', primary);
  tile.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); primary(); }
  });
  const dots = tile.querySelector('.tile-dots') as HTMLButtonElement;
  dots.addEventListener('click', (e) => { e.stopPropagation(); showModuleMenu(id, rec); });
  dots.addEventListener('mousedown', (e) => e.stopPropagation());
  dots.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
  attachLongPress(tile, () => showModuleMenu(id, rec));
  return tile;
}

/** Fills a block container with section blocks (header + tile grid). Shared by
 *  the mobile home and the desktop home pane. */
function renderSections(container: HTMLElement): void {
  const all = storage.listModules() as Array<[string, ModuleRecord]>;
  if (all.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'empty';
    empty.textContent = 'No modules yet — tap ➕ to add one.';
    container.appendChild(empty);
    return;
  }
  const order = effectiveSections();
  const groups = modulesBySection();
  const nonEmpty = order.filter((s) => (groups.get(s)?.length ?? 0) > 0);
  // One default section, not editing → plain grid (unchanged classic look).
  const showHeaders = editMode || nonEmpty.length > 1 ||
    (nonEmpty.length === 1 && nonEmpty[0] !== DEFAULT_SECTION);

  order.forEach((name, idx) => {
    const tiles = groups.get(name) ?? [];
    if (tiles.length === 0 && !editMode) return; // hide empty sections normally
    const sec = document.createElement('section');
    sec.className = 'launcher-section';
    sec.dataset.section = name;
    if (showHeaders) {
      const head = document.createElement('div');
      head.className = 'section-head';
      head.innerHTML = `
        <span class="section-name">${escapeHtml(name)}</span>
        <span class="section-actions">
          <button class="sec-btn" data-act="up" title="Move section up"${idx === 0 ? ' disabled' : ''}>▲</button>
          <button class="sec-btn" data-act="down" title="Move section down"${idx === order.length - 1 ? ' disabled' : ''}>▼</button>
          <button class="sec-btn" data-act="menu" title="Rename or delete">⋮</button>
        </span>`;
      head.querySelector('[data-act="up"]')?.addEventListener('click', () => { moveSection(name, -1); render(); });
      head.querySelector('[data-act="down"]')?.addEventListener('click', () => { moveSection(name, 1); render(); });
      head.querySelector('[data-act="menu"]')?.addEventListener('click', () => openSectionMenu(name));
      sec.appendChild(head);
    }
    const grid = document.createElement('div');
    grid.className = 'launcher-grid';
    grid.dataset.section = name;
    if (tiles.length === 0) {
      const hint = document.createElement('p');
      hint.className = 'section-empty';
      hint.textContent = 'Empty — move modules here from a tile’s ⋮ menu.';
      grid.appendChild(hint);
    } else {
      for (const [id, rec] of tiles) grid.appendChild(buildTile(id, rec));
    }
    sec.appendChild(grid);
    container.appendChild(sec);
  });

  if (editMode) {
    const addRow = document.createElement('button');
    addRow.className = 'add-section-row';
    addRow.textContent = '＋ New section';
    addRow.addEventListener('click', () =>
      promptText('New section', '', (v) => { addSectionNamed(v); render(); }));
    container.appendChild(addRow);
  }

  // Edit mode: enable drag-to-rearrange on this freshly built container.
  if (editMode) {
    container.addEventListener('mousedown', onDragStart);
    container.addEventListener('touchstart', onDragStart, { passive: true });
    ensureDragListeners();
  }
}

// ─── Drag-to-rearrange (edit mode) ───────────────────────────────────────────
// Pointer-based, mirroring the Android v3 launcher: drag a tile between/within
// sections, or grab a section name to reorder whole sections. HTML5 DnD is
// avoided because it's unreliable on touch.

type DragState =
  | { kind: 'tile'; tile: HTMLElement; startX: number; startY: number; moved: boolean; ghost: HTMLElement | null }
  | { kind: 'section'; el: HTMLElement; startY: number; moved: boolean };

let dragState: DragState | null = null;
let dragDocBound = false;
/** Set when a drag actually moved, so the trailing click doesn't open a sheet. */
let justDragged = false;

function ensureDragListeners(): void {
  if (dragDocBound) return;
  dragDocBound = true;
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);
  document.addEventListener('touchmove', onDragMove, { passive: false });
  document.addEventListener('touchend', onDragEnd);
  // Capture phase so a post-drag click is swallowed before tile handlers run.
  document.addEventListener('click', (e) => {
    if (justDragged) { justDragged = false; e.stopPropagation(); e.preventDefault(); }
  }, true);
}

function dragPoint(e: MouseEvent | TouchEvent): { clientX: number; clientY: number } {
  if ('touches' in e && e.touches.length) return e.touches[0];
  return e as MouseEvent;
}

function onDragStart(e: MouseEvent | TouchEvent): void {
  if (!editMode || dragState) return;
  const target = e.target as HTMLElement;
  if (target.closest('.tile-dots')) return;          // ⋮ button is its own thing
  if (document.querySelector('.overlay')) return;    // not while a sheet is open
  // Grabbing the section name reorders whole sections.
  const name = target.closest('.section-name') as HTMLElement | null;
  if (name) {
    const sectionEl = name.closest('.launcher-section') as HTMLElement | null;
    if (sectionEl) {
      if (e.type === 'mousedown') e.preventDefault();
      dragState = { kind: 'section', el: sectionEl, startY: dragPoint(e).clientY, moved: false };
    }
    return;
  }
  const tile = target.closest('.tile') as HTMLElement | null;
  if (!tile) return;
  // Stop the mousedown from starting a text selection on the tile name.
  // (touchstart is passive, so selection there is handled by user-select:none.)
  if (e.type === 'mousedown') e.preventDefault();
  const p = dragPoint(e);
  dragState = { kind: 'tile', tile, startX: p.clientX, startY: p.clientY, moved: false, ghost: null };
}

function onDragMove(e: MouseEvent | TouchEvent): void {
  if (!dragState) return;
  if (dragState.kind === 'section') { onSectionDragMove(e); return; }
  const p = dragPoint(e);
  const dx = p.clientX - dragState.startX, dy = p.clientY - dragState.startY;
  if (!dragState.moved && Math.hypot(dx, dy) < 6) return;
  if (!dragState.moved) {
    dragState.moved = true;
    dragState.tile.classList.add('dragging');
    const ghost = dragState.tile.cloneNode(true) as HTMLElement;
    ghost.style.cssText = 'position:fixed;pointer-events:none;z-index:9999;opacity:0.85;transform:scale(1.05);margin:0';
    document.body.appendChild(ghost);
    dragState.ghost = ghost;
  }
  if (e.cancelable) e.preventDefault();
  const { tile, ghost } = dragState;
  if (ghost) {
    const r = tile.getBoundingClientRect();
    ghost.style.left = (p.clientX - r.width / 2) + 'px';
    ghost.style.top = (p.clientY - r.height / 2) + 'px';
  }
  const under = document.elementFromPoint(p.clientX, p.clientY) as HTMLElement | null;
  const overTile = under?.closest('.tile:not(.dragging)') as HTMLElement | null;
  const overGrid = under?.closest('.launcher-grid') as HTMLElement | null;
  if (overTile && overTile.parentNode) {
    const r = overTile.getBoundingClientRect();
    const after = (p.clientX - r.left) > r.width / 2;
    overTile.parentNode.insertBefore(tile, after ? overTile.nextSibling : overTile);
  } else if (overGrid) {
    // Empty section grid (only the hint paragraph) — drop the tile in.
    if (!overGrid.querySelector('.tile')) {
      overGrid.querySelector('.section-empty')?.remove();
      overGrid.appendChild(tile);
    }
  }
}

function onDragEnd(): void {
  if (!dragState) return;
  if (dragState.kind === 'section') {
    dragState.el.classList.remove('section-dragging');
    if (dragState.moved) { markDragged(); commitSectionOrder(); }
    dragState = null;
    return;
  }
  dragState.ghost?.remove();
  if (dragState.moved) {
    dragState.tile.classList.remove('dragging');
    markDragged();
    commitDragOrder();
  }
  dragState = null;
}

/** Flag a real drag so the trailing click is swallowed; auto-clear in case
 *  no click follows (e.g. the target was rebuilt by a re-render). */
function markDragged(): void {
  justDragged = true;
  window.setTimeout(() => { justDragged = false; }, 350);
}

function onSectionDragMove(e: MouseEvent | TouchEvent): void {
  if (dragState?.kind !== 'section') return;
  const ds = dragState;
  const p = dragPoint(e);
  if (!ds.moved && Math.abs(p.clientY - ds.startY) < 6) return;
  if (!ds.moved) { ds.moved = true; ds.el.classList.add('section-dragging'); }
  if (e.cancelable) e.preventDefault();
  const root = ds.el.parentElement;
  if (!root) return;
  const others = Array.from(root.querySelectorAll(':scope > .launcher-section'))
    .filter((s) => s !== ds.el) as HTMLElement[];
  let placed = false;
  for (const s of others) {
    const r = s.getBoundingClientRect();
    if (p.clientY < r.top + r.height / 2) { root.insertBefore(ds.el, s); placed = true; break; }
  }
  if (!placed) {
    const addRow = root.querySelector(':scope > .add-section-row');
    if (addRow) root.insertBefore(ds.el, addRow);
    else root.appendChild(ds.el);
  }
}

/** Persist the new section order from the DOM, then re-render. */
function commitSectionOrder(): void {
  if (dragState?.kind !== 'section') return;
  const root = dragState.el.parentElement;
  if (!root) return;
  const order = Array.from(root.querySelectorAll(':scope > .launcher-section'))
    .map((s) => (s as HTMLElement).dataset.section)
    .filter((s): s is string => !!s);
  if (order.length) saveSectionList(order);
  render();
}

/** Persist per-tile section + global order from the DOM. No re-render — the
 *  DOM already reflects the final arrangement, which avoids a click-vs-rebuild
 *  race; the saved order is reapplied on the next natural render. */
function commitDragOrder(): void {
  const order: string[] = [];
  document.querySelectorAll('.launcher-grid[data-section]').forEach((grid) => {
    const sec = (grid as HTMLElement).dataset.section!;
    grid.querySelectorAll('.tile').forEach((t) => {
      const id = (t as HTMLElement).dataset.moduleId;
      if (!id) return;
      order.push(id);
      const rec = storage.getModule(id) as ModuleRecord | undefined;
      if (rec && moduleSection(rec) !== sec) setModuleSection(id, sec);
    });
  });
  saveModuleOrder(order);
}

function openSectionMenu(name: string): void {
  const overlay = openOverlay(`Section: ${name}`);
  const body = overlay.querySelector('.overlay-body')!;
  const fallback = effectiveSections().filter((s) => s !== name)[0] ?? DEFAULT_SECTION;
  body.innerHTML = `
    <div class="ss-actions" style="flex-direction:column;gap:8px">
      <button id="sec-rename" style="width:100%">✏️ Rename section</button>
      <button id="sec-delete" style="width:100%;background:#a33;color:#fff">🗑️ Delete section</button>
    </div>
    <p class="ss-hint" style="margin-top:8px">Deleting moves its modules to “${escapeHtml(fallback)}”.</p>`;
  const close = (): void => { overlay.dispatchEvent(new CustomEvent('overlay-close')); overlay.remove(); };
  body.querySelector('#sec-rename')!.addEventListener('click', () => {
    close();
    promptText('Rename section', name, (v) => { renameSectionNamed(name, v); render(); });
  });
  body.querySelector('#sec-delete')!.addEventListener('click', () => {
    close();
    deleteSectionNamed(name);
    render();
  });
}

/** Minimal single-input overlay used for naming/renaming sections. */
function promptText(title: string, initial: string, onOk: (value: string) => void): void {
  const overlay = openOverlay(title);
  const body = overlay.querySelector('.overlay-body')!;
  body.innerHTML = `
    <div class="field">
      <input class="ss-input" id="pt-input" value="${escapeHtml(initial)}" placeholder="Name" autocomplete="off" autocapitalize="off"/>
    </div>
    <div class="ss-actions" style="margin-top:8px"><button id="pt-ok" style="flex:1">Save</button></div>`;
  const input = body.querySelector('#pt-input') as HTMLInputElement;
  const ok = (): void => {
    const v = input.value.trim();
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
    if (v) onOk(v);
  };
  body.querySelector('#pt-ok')!.addEventListener('click', ok);
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') ok(); });
  setTimeout(() => input.focus(), 30);
}

function toggleEditMode(): void {
  editMode = !editMode;
  render();
}

function buildEditBanner(): HTMLElement {
  const bar = document.createElement('div');
  bar.id = 'edit-banner';
  bar.innerHTML = `
    <span class="eb-text">Editing layout<small>Drag tiles between sections · drag a ⠿ section name to reorder</small></span>
    <button class="eb-done">Done</button>`;
  bar.querySelector('.eb-done')!.addEventListener('click', toggleEditMode);
  return bar;
}

// ─── Desktop layout (sidebar + module pane) ──────────────────────────────────

function renderDesktop(root: HTMLElement): void {
  const openId = openModuleIdFromHash();
  const openRec = openId ? storage.getModule(openId) as ModuleRecord | undefined : undefined;

  root.innerHTML = `
    <div class="desktop-layout">
      <aside class="sidebar">
        <div class="side-head"><h1>Shell</h1></div>
        <nav class="side-list" id="side-list"></nav>
        <div class="side-foot">
          <button class="side-action" id="side-add">➕ Add a module</button>
          <button class="side-action" id="side-edit"
            ${openId ? 'disabled title="Go to Home to edit the layout"' : ''}>${editMode ? '✓ Done editing' : '✏️ Edit layout'}</button>
          <button class="side-action" id="side-settings">⚙️ Settings</button>
          <button class="sync-pill" id="sync-pill" title="Tap to sync now">
            <span class="sync-dot"></span><span id="sync-text">local</span>
          </button>
        </div>
      </aside>
      <main class="pane" id="pane"></main>
    </div>
  `;

  wireOwnerUnlock(root.querySelector('.side-head h1'));

  const list = root.querySelector('#side-list')!;
  const addRow = (icon: string, name: string, active: boolean, onClick: () => void): HTMLButtonElement => {
    const row = document.createElement('button');
    row.className = 'side-row';
    row.dataset.active = active ? '1' : '0';
    row.innerHTML = `<span class="side-icon">${escapeHtml(icon)}</span><span class="side-name">${escapeHtml(name)}</span>`;
    row.addEventListener('click', onClick);
    list.appendChild(row);
    return row;
  };
  const addSectionLabel = (name: string): void => {
    const h = document.createElement('div');
    h.className = 'side-section';
    h.textContent = name;
    list.appendChild(h);
  };
  addRow('🏠', 'Home', !openRec, () => setOpenModule(null));
  // Group sidebar rows by section, mirroring the home pane.
  const groups = modulesBySection();
  const order = effectiveSections();
  const multi = order.filter((s) => (groups.get(s)?.length ?? 0) > 0).length > 1;
  for (const name of order) {
    const tiles = groups.get(name) ?? [];
    if (tiles.length === 0 && !editMode) continue;
    if (multi || editMode || name !== DEFAULT_SECTION) addSectionLabel(name);
    for (const [id, rec] of tiles) {
      const row = addRow(rec.icon ?? tileEmoji(id), rec.name, id === openId, () => setOpenModule(id));
      row.addEventListener('contextmenu', (e) => { e.preventDefault(); showModuleMenu(id, rec); });
    }
  }

  const pane = root.querySelector('#pane') as HTMLElement;
  if (openId && openRec) renderModulePane(pane, openId, openRec);
  else renderHomePane(pane);
  if (editMode) root.appendChild(buildEditBanner());

  root.querySelector('#side-add')!.addEventListener('click', showAddSheet);
  root.querySelector('#side-edit')!.addEventListener('click', toggleEditMode);
  root.querySelector('#side-settings')!.addEventListener('click', showSettings);
  root.querySelector('#sync-pill')!.addEventListener('click', () => { void manualSync(); });
}

function renderHomePane(pane: HTMLElement): void {
  pane.innerHTML = `<div class="pane-home"><div class="sections-wrap" id="home-sections"></div></div>`;
  renderSections(pane.querySelector('#home-sections') as HTMLElement);
}

function renderModulePane(pane: HTMLElement, id: string, rec: ModuleRecord): void {
  // Width: explicit user choice wins; otherwise the module's own manifest
  // (Builder modules declare viewport: mobile|desktop|both) picks the default.
  let saved = localStorage.getItem(`shell.viewport.${id}`);
  if (saved !== 'wide' && saved !== 'mobile') {
    try {
      const man = JSON.parse(storage.get({ moduleId: id, key: '__published__::manifest' }) || 'null') as { viewport?: string } | null;
      saved = (man?.viewport === 'desktop' || man?.viewport === 'both') ? 'wide' : 'mobile';
    } catch { saved = 'mobile'; }
  }
  const mode = saved === 'wide' ? 'wide' : 'mobile';
  pane.innerHTML = `
    <header class="pane-head">
      <button class="icon-btn" id="pane-back" title="Back to home">←</button>
      <span class="pane-title">${escapeHtml(rec.icon ?? '🧩')} ${escapeHtml(rec.name)}</span>
      <span style="flex:1"></span>
      <div class="seg pane-width-seg" id="pane-width" title="Module width">
        <button data-mode="mobile" data-active="${mode === 'mobile' ? '1' : '0'}" title="Phone width">📱</button>
        <button data-mode="wide" data-active="${mode === 'wide' ? '1' : '0'}" title="Full width">🖥️</button>
      </div>
    </header>
    <div class="frame-wrap ${mode}" id="frame-wrap">
      <iframe class="module-frame" id="module-frame" title="${escapeHtml(rec.name)}"
        allow="camera; microphone; geolocation; clipboard-read; clipboard-write"></iframe>
    </div>
  `;
  const frame = pane.querySelector('#module-frame') as HTMLIFrameElement;
  const url = new URL('module.html', window.location.href);
  url.searchParams.set('id', id);
  url.searchParams.set('embed', '1');
  frame.src = url.toString();

  pane.querySelector('#pane-back')!.addEventListener('click', () => setOpenModule(null));
  pane.querySelectorAll('#pane-width button').forEach((b) => b.addEventListener('click', () => {
    const m = (b as HTMLElement).dataset.mode!;
    localStorage.setItem(`shell.viewport.${id}`, m);
    pane.querySelectorAll('#pane-width button').forEach((x) =>
      (x as HTMLElement).dataset.active = x === b ? '1' : '0');
    // No reload — the module's CSS media queries respond to the new width.
    (pane.querySelector('#frame-wrap') as HTMLElement).className = `frame-wrap ${m}`;
  }));
}

/** Pushes new theme tokens into every open module iframe (pane + warm-ups). */
function broadcastTheme(preset: ThemePreset): void {
  const css = buildTokenCss(preset);
  document.querySelectorAll('iframe').forEach((f) => {
    try {
      (f as HTMLIFrameElement).contentWindow?.postMessage(
        { __shell: true, type: 'theme', css },
        window.location.origin,
      );
    } catch { /* frame mid-navigation — it'll read the saved theme on load */ }
  });
}

/**
 * The ➕ sheet — mirrors the v3 launcher's add flow: build a module with AI
 * (via the Module Builder) or paste code by hand.
 */
function showAddSheet(): void {
  const overlay = openOverlay('Add a module');
  const body = overlay.querySelector('.overlay-body')!;
  body.innerHTML = `
    <button class="row" id="add-ai">
      <span class="row-label"><span class="row-icon">✨</span>
        <span>Build it with AI<span class="row-sub" style="display:block">Describe it — the Module Builder writes the code</span></span>
      </span><span class="chevron">›</span>
    </button>
    <button class="row" id="add-upload">
      <span class="row-label"><span class="row-icon">📁</span>
        <span>Upload a file<span class="row-sub" style="display:block">An .html file, or a Shell .json export (with its data)</span></span>
      </span><span class="chevron">›</span>
    </button>
    <button class="row" id="add-paste">
      <span class="row-label"><span class="row-icon">📋</span>
        <span>Paste module code<span class="row-sub" style="display:block">Already have the HTML? Add it directly</span></span>
      </span><span class="chevron">›</span>
    </button>
  `;
  const close = (): void => {
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
  };
  body.querySelector('#add-ai')!.addEventListener('click', () => {
    close();
    openModule('promptgenerator');
  });
  body.querySelector('#add-upload')!.addEventListener('click', () => {
    close();
    importModuleFromFile();
  });
  body.querySelector('#add-paste')!.addEventListener('click', () => {
    close();
    showCreateModule();
  });
}

function renderSyncPill(snap: ReturnType<typeof syncState.snapshot.valueOf> & { status: SyncStatus; lastSyncMs: number; lastError: string }): void {
  const el = document.getElementById('sync-pill');
  const text = document.getElementById('sync-text');
  if (!el || !text) return;
  const { status, lastSyncMs } = snap;
  el.dataset.state = status;
  switch (status) {
    case 'SYNCING':
      text.textContent = 'syncing…'; break;
    case 'OK':
      text.textContent = ago(lastSyncMs); break;
    case 'ERROR':
      text.textContent = 'sync error'; break;
    case 'IDLE':
      text.textContent = lastSyncMs ? ago(lastSyncMs) : 'idle'; break;
    case 'NOT_CONFIGURED':
    default:
      text.textContent = 'local'; break;
  }
}

function ago(ms: number): string {
  if (!ms) return 'never';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Settings overlay (Cloud Sync) ───────────────────────────────────────────

function showSettings(): void {
  const overlay = openOverlay('Settings');
  overlay.classList.add('settings-overlay');
  const body = overlay.querySelector('.overlay-body')!;

  // Experience level — Beginner/Advanced. Gates power tools (Sync Inspector,
  // Backups, …). Stored per-device in localStorage 'shell.experience'.
  const advNow = localStorage.getItem('shell.experience') === 'advanced';
  const expSection = document.createElement('section');
  expSection.className = 'settings-section';
  expSection.innerHTML = `
    <h3>Experience level</h3>
    <div class="seg" id="exp-seg">
      <button data-lvl="beginner" data-active="${advNow ? '0' : '1'}">🌱 Beginner</button>
      <button data-lvl="advanced" data-active="${advNow ? '1' : '0'}">⚙️ Advanced</button>
    </div>
    <p class="ss-hint" style="margin-top:8px">${advNow
      ? 'Everything is unlocked — Sync Inspector, Backups and other power tools.'
      : 'The essentials. Switch to Advanced to reveal power tools like the Sync Inspector.'}</p>`;
  body.appendChild(expSection);
  expSection.querySelectorAll('#exp-seg button').forEach((b) => b.addEventListener('click', () => {
    const lvl = (b as HTMLElement).dataset.lvl === 'advanced' ? 'advanced' : 'beginner';
    if ((localStorage.getItem('shell.experience') === 'advanced' ? 'advanced' : 'beginner') === lvl) return;
    localStorage.setItem('shell.experience', lvl);
    showToast(lvl === 'advanced' ? '⚙️ Advanced mode on' : '🌱 Beginner mode on');
    // Re-open Settings so Advanced-gated sections appear/disappear immediately.
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
    showSettings();
  }));

  // Theme — six curated presets, same palette set as the Android v3 launcher.
  const themeSection = document.createElement('section');
  themeSection.className = 'settings-section';
  themeSection.innerHTML = `
    <h3>Theme</h3>
    <div class="preset-grid" id="theme-presets"></div>
    <label style="display:flex;align-items:flex-start;gap:10px;margin-top:14px;cursor:pointer;font-size:14px;color:var(--label)">
      <input type="checkbox" id="theme-modules" style="margin-top:2px"/>
      <span>Apply app theme to modules
        <span style="display:block;font-size:12px;color:var(--dim);margin-top:2px">Tints opened modules with your theme colours. Turn off if a module's own colours look wrong — e.g. a map view washing out.</span>
      </span>
    </label>
  `;
  body.appendChild(themeSection);

  const themeModulesChk = themeSection.querySelector('#theme-modules') as HTMLInputElement;
  themeModulesChk.checked = isModuleThemingEnabled();
  themeModulesChk.addEventListener('change', () => {
    setModuleThemingEnabled(themeModulesChk.checked);
    showToast(themeModulesChk.checked
      ? '🎨 Modules will use your theme — reopen any open module'
      : '🚫 Modules keep their own colours — reopen any open module');
  });

  const renderPresets = (): void => {
    const wrap = document.getElementById('theme-presets');
    if (!wrap) return;
    const activeId = loadTheme().id;
    wrap.innerHTML = THEME_PRESETS.map((p) => `
      <button class="preset" data-id="${p.id}" data-active="${p.id === activeId ? '1' : '0'}">
        <span class="preset-swatch">
          <span style="background:${p.bg}"></span>
          <span style="background:${p.surface}"></span>
          <span style="background:${p.accent}"></span>
        </span>
        <span class="preset-name">${escapeHtml(p.name)}</span>
      </button>`).join('');
    wrap.querySelectorAll('.preset').forEach((el) => el.addEventListener('click', () => {
      const preset = THEME_PRESETS.find((p) => p.id === (el as HTMLElement).dataset.id);
      if (!preset) return;
      saveTheme(preset);
      applyTheme(preset);
      broadcastTheme(preset);
      renderPresets();
    }));
  };
  renderPresets();

  // Install app section (only meaningful on browsers that fire beforeinstallprompt)
  const installSection = document.createElement('section');
  installSection.className = 'settings-section';
  installSection.innerHTML = `
    <h3>Install as App</h3>
    <div id="install-row"></div>
  `;
  body.appendChild(installSection);

  const renderInstallSection = (state: { available: boolean; installed: boolean }): void => {
    const row = document.getElementById('install-row');
    if (!row) return;
    if (state.installed || isStandalone()) {
      row.innerHTML = '<p class="ss-hint">✓ Shell is installed on this device.</p>';
    } else if (state.available) {
      row.innerHTML = '<div class="ss-actions"><button id="install-now">📲 Install Shell</button></div>';
      document.getElementById('install-now')!.addEventListener('click', async () => {
        const outcome = await installPrompt.trigger();
        if (outcome === 'accepted') showToast('🎉 Installing Shell…');
      });
    } else if (isIos()) {
      // iOS never fires beforeinstallprompt — guide the manual Add to Home Screen.
      row.innerHTML =
        '<p class="ss-hint" style="margin-bottom:6px">Add Shell to your Home Screen for a full-screen app:</p>' +
        '<ol class="ss-hint" style="margin:0;padding-left:18px;line-height:1.8">' +
        '<li>Tap the <b>Share</b> button <span style="font-size:15px">􀈂</span> in Safari\'s toolbar.</li>' +
        '<li>Choose <b>Add to Home Screen</b> <span style="font-size:15px">􀎒</span>.</li>' +
        '<li>Tap <b>Add</b> — Shell appears as its own icon.</li></ol>';
    } else {
      row.innerHTML = '<p class="ss-hint">Install isn\'t available in this browser yet. ' +
        'On Chrome, install is offered after a brief engagement — refresh once you\'ve ' +
        'been on the page for a moment and look for the install icon in the address bar.</p>';
    }
  };
  const unsubInstall = installPrompt.subscribe(renderInstallSection);

  // AI Profiles — v3 flow: list with Edit/✕, one form sheet with provider
  // segments and live "Find models" discovery.
  const aiSection = document.createElement('section');
  aiSection.className = 'settings-section';
  aiSection.innerHTML = `
    <h3>AI Profiles</h3>
    <p class="ss-hint">Profiles store your API key + model per provider.
      Modules call Shell.promptAI() using a named profile.</p>
    <div id="ai-profile-list"></div>
    <div class="ss-actions"><button id="ai-add">+ Add AI profile</button></div>
  `;
  body.appendChild(aiSection);

  const refreshAiProfiles = (): void => {
    const list = document.getElementById('ai-profile-list');
    if (!list) return;
    const profiles = getAiProfiles();
    const names = Object.keys(profiles);
    if (names.length === 0) {
      list.innerHTML = '<p class="ss-hint" style="margin-bottom:4px">No profiles yet. Add one to use AI features in your modules.</p>';
      return;
    }
    list.innerHTML = names.map((name) => {
      const p = profiles[name];
      const meta = AI_PROVIDERS[p.provider] ?? { label: p.provider || 'AI', color: '#888888' };
      return `<div class="ss-row" style="align-items:center">
        <span style="min-width:0">${escapeHtml(name)}
          <span class="badge" style="background:${meta.color}22;color:${meta.color}">${escapeHtml(meta.label)}</span>
          <span style="display:block;font-size:11px;opacity:0.7">${escapeHtml(p.model || '—')}</span>
        </span>
        <span style="display:flex;gap:6px;flex-shrink:0">
          <button class="mini-btn" data-edit-profile="${escapeHtml(name)}">Edit</button>
          <button class="mini-btn danger" data-del-profile="${escapeHtml(name)}">✕</button>
        </span>
      </div>`;
    }).join('');
    list.querySelectorAll('[data-edit-profile]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = (btn as HTMLElement).dataset.editProfile!;
        openProfileForm(name, getAiProfiles()[name] ?? null, refreshAiProfiles);
      });
    });
    list.querySelectorAll('[data-del-profile]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const name = (btn as HTMLElement).dataset.delProfile!;
        if (!confirm(`Delete "${name}"? Modules using this profile will stop working until you add it again.`)) return;
        deleteAiProfile(name);
        refreshAiProfiles();
        showToast('Profile deleted.');
      });
    });
  };
  aiSection.querySelector('#ai-add')!.addEventListener('click', () =>
    openProfileForm(null, null, refreshAiProfiles));
  refreshAiProfiles();

  // Auto-sync section
  const autoSyncSection = document.createElement('section');
  autoSyncSection.className = 'settings-section';
  autoSyncSection.innerHTML = `
    <h3>Auto-sync</h3>
    <p class="ss-hint">Sync runs on app launch, when the tab regains focus, and at a regular interval. Tap the cloud pill on the launcher to sync manually anytime.</p>
    <label style="display:flex;align-items:center;gap:8px;font-size:14px;color:#ccc;margin-bottom:10px">
      <input type="checkbox" id="as-enabled" /> Enable auto-sync
    </label>
    <div style="display:flex;align-items:center;gap:8px;font-size:13px;color:#aaa">
      <span>Interval (minutes):</span>
      <input id="as-interval" class="ss-input" type="number" min="1" max="60" style="width:80px" />
    </div>
  `;
  body.appendChild(autoSyncSection);

  const enabledChk = document.getElementById('as-enabled') as HTMLInputElement;
  const intervalInp = document.getElementById('as-interval') as HTMLInputElement;
  enabledChk.checked = isAutoSyncEnabled();
  intervalInp.value = String(getAutoSyncIntervalMinutes());
  enabledChk.addEventListener('change', () => {
    setAutoSyncEnabled(enabledChk.checked);
    showToast(enabledChk.checked ? '🔄 Auto-sync on' : '🛑 Auto-sync off');
  });
  intervalInp.addEventListener('change', () => {
    const v = Number(intervalInp.value);
    if (Number.isFinite(v) && v >= 1) {
      setAutoSyncIntervalMinutes(v);
      showToast(`⏱️ Auto-sync every ${v} min`);
    } else {
      intervalInp.value = String(getAutoSyncIntervalMinutes());
    }
  });

  // Drive tools + Automatic backups + GitHub — advanced only. Declared out here
  // so the section-grouping pass below can place them under the "Cloud" header.
  // These wire via each section's own querySelector, so they don't need to be in
  // the DOM yet (the grouping pass appends them).
  let driveToolsSection: HTMLElement | null = null;
  let backupsSection: HTMLElement | null = null;
  let ghSection: HTMLElement | null = null;
  if (localStorage.getItem('shell.experience') === 'advanced') {
    // Drive tools — Inspect Drive + Sweep stale files.
    driveToolsSection = document.createElement('section');
    driveToolsSection.className = 'settings-section';
    driveToolsSection.innerHTML = `
      <h3>Drive tools</h3>
      <p class="ss-hint">Browse what's stored on Drive, or clear out stale tombstones &amp; orphaned data.</p>
      <div class="ss-actions">
        <button id="dt-inspect">🔍 Inspect Drive</button>
        <button id="dt-sweep">🧹 Sweep stale files</button>
      </div>`;
    driveToolsSection.querySelector('#dt-inspect')!.addEventListener('click', () => { void showSyncInspector('files'); });
    driveToolsSection.querySelector('#dt-sweep')!.addEventListener('click', () => {
      const p = syncManager.get();
      if (!p || !p.isAuthenticated()) { showToast('Connect Google Drive first'); return; }
      void runSweepFlow(p);
    });

    // Automatic backups — rolling checkpoints + Timeline recovery.
    backupsSection = document.createElement('section');
    backupsSection.className = 'settings-section';
    backupsSection.innerHTML = `
      <h3>Automatic backups</h3>
      <p class="ss-hint">After each sync that changes something, Shell keeps a rolling snapshot of your modules + data on Drive.</p>
      <label style="display:flex;align-items:center;gap:8px;font-size:14px;color:var(--label);margin-bottom:10px">
        <input type="checkbox" id="cp-enabled"/> Keep backups
      </label>
      <div style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--dim);margin-bottom:12px">
        <span>Keep last</span>
        <input id="cp-keep" class="ss-input" type="number" min="1" max="50" style="width:80px"/>
        <span>saves</span>
      </div>
      <div class="ss-actions"><button id="cp-timeline">🕑 Timeline &amp; recovery</button></div>`;
    const cpEnabled = backupsSection.querySelector('#cp-enabled') as HTMLInputElement;
    const cpKeep = backupsSection.querySelector('#cp-keep') as HTMLInputElement;
    cpEnabled.checked = isCheckpointingEnabled();
    cpKeep.value = String(getCheckpointKeep());
    cpEnabled.addEventListener('change', () => {
      setCheckpointingEnabled(cpEnabled.checked);
      showToast(cpEnabled.checked ? '🗂️ Backups on' : '🛑 Backups off');
    });
    cpKeep.addEventListener('change', () => {
      const v = Number(cpKeep.value);
      if (Number.isFinite(v) && v >= 1) { setCheckpointKeep(v); showToast(`Keeping last ${getCheckpointKeep()} saves`); }
      cpKeep.value = String(getCheckpointKeep());
    });
    backupsSection.querySelector('#cp-timeline')!.addEventListener('click', () => {
      const p = syncManager.get();
      if (!p || !p.isAuthenticated()) { showToast('Connect Google Drive first'); return; }
      void showSyncInspector('timeline');
    });

    // GitHub — app-level personal access token for pulling module code from
    // (private) repos. Stored on this device only, like an AI key.
    ghSection = document.createElement('section');
    ghSection.className = 'settings-section';
    ghSection.innerHTML = `
      <h3>GitHub</h3>
      <p class="ss-hint">A personal access token lets modules pull their code from your GitHub repos (private included). Needs the <code>repo</code> scope (or <code>Contents: read</code> for a fine-grained token). Stored on this device only.</p>
      <div style="display:flex;gap:8px">
        <input class="ss-input" id="gh-pat" type="password" autocomplete="off" autocapitalize="off" spellcheck="false"
          placeholder="ghp_… (leave blank for public repos)" style="flex:1;min-width:0"/>
        <button class="mini-btn" id="gh-pat-save" style="flex-shrink:0">Save</button>
      </div>`;
    const ghPat = ghSection.querySelector('#gh-pat') as HTMLInputElement;
    ghPat.value = getGithubPat();
    ghSection.querySelector('#gh-pat-save')!.addEventListener('click', () => {
      setGithubPat(ghPat.value);
      showToast(ghPat.value.trim() ? '🐙 GitHub token saved' : 'GitHub token cleared');
    });
  }

  // Google Drive — decluttered to match the Android card: status + account +
  // what's syncing from this device, then Sync now / Sign out (or Connect).
  // Inspect Drive + Sweep moved to "Drive tools"; Self-test dropped from the UI.
  const section = document.createElement('section');
  section.className = 'settings-section';
  section.innerHTML = `
    <h3>Google Drive</h3>
    <div class="ss-row"><span>Status:</span><span id="ss-status">—</span></div>
    <div class="ss-row"><span>Account:</span><span id="ss-account">—</span></div>
    <div class="ss-row" id="ss-synced-row" style="display:none"><span>From this device:</span><span id="ss-synced">—</span></div>
    <div class="ss-actions">
      <button id="ss-connect">Connect Google Drive</button>
      <button id="ss-syncnow" style="display:none">🔁 Sync now</button>
      <button id="ss-disconnect" style="display:none">Sign out</button>
    </div>
    <pre id="ss-result" class="ss-result"></pre>
  `;
  body.appendChild(section);

  if (!isGoogleSyncConfigured()) {
    const warn = document.createElement('p');
    warn.className = 'ss-warn';
    warn.textContent =
      'Google Drive sync isn\'t configured for this build. ' +
      'Set VITE_GOOGLE_CLIENT_ID in your Cloudflare Pages environment.';
    body.appendChild(warn);
  }

  // Shared with others (F2) — bind a module's data to a visible shared Drive
  // folder; invites happen in the Drive UI; record-level merge keeps everyone
  // converged. Mirrors the Android Settings card. Advanced-only.
  let sharedSection: HTMLElement | null = null;
  if (localStorage.getItem('shell.experience') === 'advanced') {
  sharedSection = document.createElement('section');
  sharedSection.className = 'settings-section';
  const sharedSectionRef = sharedSection;
  const renderSharedSection = (): void => {
    const provider = syncManager.get();
    const supported = !!(provider && provider.isAuthenticated() && provider.supportsSharedFolders?.());
    const bindings = getSharedBindings();
    const ids = Object.keys(bindings);
    const moduleName = (id: string): string => {
      const rec = storage.getModule(id) as ModuleRecord | undefined;
      return rec?.name ?? id;
    };
    const options = (storage.listModules() as Array<[string, ModuleRecord]>)
      .map(([id, rec]) => `<option value="${escapeHtml(id)}">${escapeHtml(rec.name || id)}</option>`)
      .join('');
    sharedSectionRef.innerHTML = `
      <h3>Shared with others</h3>
      ${supported ? `
        ${ids.length ? ids.map((id) => `
          <div class="ss-row">
            <span>👥 ${escapeHtml(moduleName(id))}</span>
            <span>
              ${escapeHtml(bindings[id]!.folderName || 'Shared folder')}
              <button data-shd-invite="${escapeHtml(id)}">Invite</button>
              <button data-shd-stop="${escapeHtml(id)}">Stop</button>
            </span>
          </div>`).join('')
        : '<p class="ss-hint">Nothing shared yet. Share a module\'s data and everyone you invite sees the same entries — a family budget, a joint tracker, a shared list.</p>'}
        <div class="ss-row"><span>Module:</span><select id="shd-module">${options}</select></div>
        <div class="ss-actions">
          <button id="shd-share">📤 Share (create folder)</button>
          <button id="shd-join">📥 Join from a link…</button>
        </div>
        <pre id="shd-result" class="ss-result"></pre>`
      : '<p class="ss-hint">Connect Google Drive above to share a module\'s data with family or friends.</p>'}
    `;
    const shdResult = (t: string): void => {
      const el = sharedSectionRef.querySelector('#shd-result');
      if (el) el.textContent = t;
    };
    sharedSectionRef.querySelectorAll('[data-shd-invite]').forEach((btn) => btn.addEventListener('click', () => {
      const b = bindings[(btn as HTMLElement).dataset.shdInvite!];
      if (b?.link) window.open(b.link, '_blank');
    }));
    sharedSectionRef.querySelectorAll('[data-shd-stop]').forEach((btn) => btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset.shdStop!;
      if (!window.confirm('Stop sharing? This device keeps its data; others keep using the folder.')) return;
      unbindSharedModule(id);
      refreshAutoSyncCadence();
      renderSharedSection();
    }));
    sharedSectionRef.querySelector('#shd-share')?.addEventListener('click', () => {
      void (async () => {
        const p = syncManager.get();
        if (!p) return;
        const id = (sharedSectionRef.querySelector('#shd-module') as HTMLSelectElement | null)?.value;
        if (!id) { shdResult('Pick a module first.'); return; }
        shdResult('Creating the shared folder… (Google may ask for permission once)');
        if (!(await p.requestSharedScope?.())) { shdResult('✗ Drive permission not granted'); return; }
        const info = await p.createSharedFolder?.(`Shell Shared — ${moduleName(id)}`);
        if (!info) { shdResult("✗ Couldn't create the shared folder"); return; }
        bindSharedModule(id, { folderId: info.id, folderName: info.name, link: info.link, knownKeys: [], lastMergedMs: 0 });
        refreshAutoSyncCadence();
        void syncManager.syncNow();
        renderSharedSection();
        window.open(info.link, '_blank');   // straight to Drive's share UI to invite
      })();
    });
    sharedSectionRef.querySelector('#shd-join')?.addEventListener('click', () => {
      void (async () => {
        const p = syncManager.get();
        if (!p) return;
        const id = (sharedSectionRef.querySelector('#shd-module') as HTMLSelectElement | null)?.value;
        if (!id) { shdResult('Pick a module first.'); return; }
        const raw = window.prompt('Paste the shared folder link (or its ID):');
        if (!raw) return;
        const m = /\/folders\/([A-Za-z0-9_-]+)/.exec(raw.trim()) ?? /[?&]id=([A-Za-z0-9_-]+)/.exec(raw.trim());
        const folderId = m ? m[1]! : (/^[A-Za-z0-9_-]{10,}$/.test(raw.trim()) ? raw.trim() : null);
        if (!folderId) { shdResult("✗ That doesn't look like a Drive folder link or ID"); return; }
        shdResult('Joining… (Google may ask for permission once)');
        if (!(await p.requestSharedScope?.())) { shdResult('✗ Drive permission not granted'); return; }
        const info = await p.getSharedFolderInfo?.(folderId);
        if (!info) { shdResult('✗ Folder not found — was it shared with this Google account?'); return; }
        bindSharedModule(id, { folderId: info.id, folderName: info.name, link: info.link, knownKeys: [], lastMergedMs: 0 });
        refreshAutoSyncCadence();
        void syncManager.syncNow();
        renderSharedSection();
      })();
    });
  };
  renderSharedSection();
  }

  // ── Group the sections into Android-style cards under section headers ──
  // Each block was built + wired inline above (so its listeners are attached);
  // here we re-append the same nodes in the unified order, matching the v3 app:
  //   Cloud · AI · Appearance · App  (Experience stays pinned on top).
  const mkHead = (t: string): HTMLElement => {
    const h = document.createElement('div');
    h.className = 'set-sec-head';
    h.innerHTML = `<span>${escapeHtml(t)}</span><span class="ssh-rule"></span>`;
    return h;
  };
  // On-device models is Android-only (MediaPipe). Show it greyed so the IA matches.
  const onDeviceStub = document.createElement('section');
  onDeviceStub.className = 'settings-section';
  onDeviceStub.style.opacity = '0.55';
  onDeviceStub.innerHTML =
    '<h3>On-device models</h3>' +
    '<p class="ss-hint" style="margin:0">Run AI fully offline on your phone — available in the Android app.</p>';
  const warnEl = body.querySelector('.ss-warn');
  body.innerHTML = '';
  body.appendChild(mkHead('Appearance'));
  body.appendChild(themeSection);
  body.appendChild(expSection);
  body.appendChild(mkHead('Cloud'));
  body.appendChild(section);             // Google Drive (account + Sync now / Sign out)
  if (warnEl) body.appendChild(warnEl);
  body.appendChild(autoSyncSection);     // Auto-sync, right after Google Drive
  if (sharedSection) body.appendChild(sharedSection);       // Shared with others (F2, advanced)
  if (driveToolsSection) body.appendChild(driveToolsSection);   // Inspect + Sweep
  if (backupsSection) body.appendChild(backupsSection);         // Automatic backups + Timeline
  if (ghSection) body.appendChild(ghSection);
  body.appendChild(mkHead('AI'));
  body.appendChild(aiSection);
  body.appendChild(onDeviceStub);
  body.appendChild(mkHead('App'));
  body.appendChild(installSection);

  const refresh = (): void => {
    const provider = syncManager.getActiveProviderName();
    const ready = syncManager.isReady();
    const connected = provider !== null;
    (document.getElementById('ss-status')!).textContent = connected ? syncState.snapshot.status : 'Not connected';
    (document.getElementById('ss-account')!).textContent = syncManager.getAccountEmail() ?? '—';
    // What's syncing from this device (matches the Android card).
    let dataOn = 0, codeOn = 0;
    (storage.listModules() as Array<[string, ModuleRecord]>).forEach(([, rec]) => {
      if (rec.dataSyncMode === 'sync') dataOn++;
      if (rec.codeSyncMode === 'sync') codeOn++;
    });
    (document.getElementById('ss-synced')!).textContent =
      `${dataOn} module${dataOn === 1 ? '' : 's'} syncing data · ${codeOn} syncing updates`;
    (document.getElementById('ss-synced-row') as HTMLElement).style.display = connected ? '' : 'none';
    const connectBtn = document.getElementById('ss-connect') as HTMLButtonElement;
    const syncBtn = document.getElementById('ss-syncnow') as HTMLButtonElement;
    const outBtn = document.getElementById('ss-disconnect') as HTMLButtonElement;
    connectBtn.style.display = connected ? 'none' : '';
    connectBtn.disabled = !isGoogleSyncConfigured();
    syncBtn.style.display = connected ? '' : 'none';
    syncBtn.disabled = !ready;
    outBtn.style.display = connected ? '' : 'none';
  };
  const setResult = (text: string): void => {
    (document.getElementById('ss-result')!).textContent = text;
  };

  document.getElementById('ss-connect')!.addEventListener('click', async () => {
    setResult('Opening Google sign-in…');
    const ok = await syncManager.selectProvider('google_drive');
    setResult(ok ? '✓ connected' : `✗ ${syncState.snapshot.lastError || 'sign-in cancelled'}`);
    refresh();
  });
  document.getElementById('ss-disconnect')!.addEventListener('click', async () => {
    await syncManager.disconnect();
    setResult('Signed out.');
    refresh();
  });
  document.getElementById('ss-syncnow')!.addEventListener('click', async () => {
    setResult('Syncing…');
    const r = await syncManager.syncNow();
    setResult(JSON.stringify(r, null, 2));
    refresh();
  });

  refresh();
  const unsub = syncState.subscribe(refresh);
  overlay.addEventListener('overlay-close', () => { unsub(); unsubInstall(); });
}

// ─── Sync Inspector (read-only Drive file browser) ───────────────────────────

function humanSize(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes, i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
}

function fileType(path: string): 'code' | 'data' | 'other' {
  if (path.startsWith(`${REMOTE_MODULES_PREFIX}/`)) return 'code';
  if (path.startsWith(`${REMOTE_STORAGE_PREFIX}/`)) return 'data';
  return 'other';
}

/** Parses a synced snapshot and renders its key metadata as rows. */
function renderFileDetail(json: string | null, type: 'code' | 'data' | 'other'): string {
  if (!json) return '<span class="ss-hint">Empty or unreadable.</span>';
  let o: Record<string, unknown>;
  try { o = JSON.parse(json) as Record<string, unknown>; } catch { return '<span class="ss-hint">Not valid JSON.</span>'; }
  const rows: string[] = [];
  const add = (k: string, v: string): void => { rows.push(`<div class="si-kv"><span>${escapeHtml(k)}</span><span>${escapeHtml(v)}</span></div>`); };
  const thisDevice = getOrCreateDeviceId();
  if (o.deleted) add('Status', '🪦 Tombstone — deleted');
  if (typeof o.moduleId === 'string') add('Module', o.moduleId);
  if (typeof o.deviceId === 'string') add('Last device', o.deviceId === thisDevice ? `${o.deviceId} (this device)` : o.deviceId);
  const ts = (typeof o.deletedAtMs === 'number' && o.deletedAtMs) || (typeof o.lastModifiedMs === 'number' ? o.lastModifiedMs : 0);
  if (ts) add('Snapshot time', `${new Date(ts).toLocaleString()} · ${ago(ts)}`);
  if (typeof o.schemaVersion !== 'undefined') add('Schema', String(o.schemaVersion));
  if (type === 'data' && o.keys && typeof o.keys === 'object') add('Data keys', String(Object.keys(o.keys as object).length));
  if (type === 'code' && o.files && typeof o.files === 'object') {
    const fk = Object.keys(o.files as object);
    add('Files', String(fk.length));
    const revs = fk.filter((k) => k.startsWith('revisions/')).length;
    if (revs) add('Revisions kept', String(revs));
  }
  return rows.join('') || '<span class="ss-hint">No metadata.</span>';
}

async function showSyncInspector(initialTab: 'files' | 'timeline' = 'files'): Promise<void> {
  const provider = syncManager.get();
  const overlay = openOverlay('Sync Inspector');
  const body = overlay.querySelector('.overlay-body')! as HTMLElement;
  if (!provider || !provider.isAuthenticated()) {
    body.innerHTML = '<p class="ss-hint">Connect Google Drive first.</p>';
    return;
  }
  body.innerHTML = `
    <div class="si-tabs">
      <button class="si-tab" data-tab="files" data-active="1">📁 Live files</button>
      <button class="si-tab" data-tab="timeline" data-active="0">🕑 Timeline</button>
    </div>
    <div id="si-content"></div>`;
  const content = body.querySelector('#si-content') as HTMLElement;
  const tabs = Array.from(body.querySelectorAll('.si-tab')) as HTMLElement[];
  const select = (tab: string): void => {
    tabs.forEach((t) => (t.dataset.active = t.dataset.tab === tab ? '1' : '0'));
    content.innerHTML = '';
    if (tab === 'files') void renderLiveFiles(content, provider);
    else void renderTimeline(content, provider);
  };
  tabs.forEach((t) => t.addEventListener('click', () => select(t.dataset.tab!)));
  select(initialTab);
}

async function renderLiveFiles(container: HTMLElement, provider: SyncProvider): Promise<void> {
  container.innerHTML = `
    <div class="si-toolbar"><button class="si-sweep" id="si-sweep">🧹 Sweep stale files</button></div>
    <p class="ss-hint" id="si-status">Loading Drive files…</p><div id="si-list"></div>`;
  const statusEl = container.querySelector('#si-status') as HTMLElement;
  const listEl = container.querySelector('#si-list') as HTMLElement;
  container.querySelector('#si-sweep')!.addEventListener('click', () => void runSweepFlow(provider, container));

  let files: RemoteFile[];
  try {
    files = await provider.listFiles(REMOTE_ROOT_PREFIX);
  } catch (e) {
    statusEl.textContent = `Failed to list Drive files: ${(e as Error).message}`;
    return;
  }
  // Hide checkpoint files from the live-files view — they have their own tab.
  files = files.filter((f) => !f.path.startsWith('shell/checkpoints/'));
  const total = files.reduce((s, f) => s + (f.sizeBytes || 0), 0);
  statusEl.textContent = `${files.length} file${files.length === 1 ? '' : 's'} · ${humanSize(total)} total · this device ${getOrCreateDeviceId()}`;

  if (!files.length) {
    listEl.innerHTML = '<p class="ss-hint">No files on Drive yet — run a sync first.</p>';
    return;
  }

  // Pre-read code snapshots so we can split live modules from tombstones, and
  // cache the bodies to avoid re-reading on expand. (Storage files carry no
  // tombstone — a deleted module's data file is removed outright.)
  const bodyCache = new Map<string, string | null>();
  await Promise.all(files.filter((f) => fileType(f.path) === 'code').map(async (f) => {
    try { bodyCache.set(f.path, await provider.readFile(f.path)); } catch { bodyCache.set(f.path, null); }
  }));
  const isTombstone = (f: RemoteFile): boolean => {
    const j = bodyCache.get(f.path);
    if (!j) return false;
    try { return (JSON.parse(j) as { deleted?: boolean }).deleted === true; } catch { return false; }
  };

  const groups: Array<{ title: string; type: 'code' | 'data' | 'other'; files: RemoteFile[] }> = [
    { title: '📝 Modules', type: 'code', files: files.filter((f) => fileType(f.path) === 'code' && !isTombstone(f)) },
    { title: '💾 Storage', type: 'data', files: files.filter((f) => fileType(f.path) === 'data') },
    { title: '🪦 Tombstones', type: 'code', files: files.filter((f) => fileType(f.path) === 'code' && isTombstone(f)) },
    { title: '📄 Other', type: 'other', files: files.filter((f) => fileType(f.path) === 'other') },
  ];

  listEl.innerHTML = '';
  const icon = (t: string): string => (t === 'code' ? '📝' : t === 'data' ? '💾' : '📄');
  for (const grp of groups) {
    if (!grp.files.length) continue;
    const header = document.createElement('div');
    header.className = 'si-group';
    header.textContent = `${grp.title} (${grp.files.length})`;
    listEl.appendChild(header);

    for (const f of grp.files.sort((a, b) => a.path.localeCompare(b.path))) {
      const name = f.path.replace(/^shell\//, '');
      const row = document.createElement('div');
      row.className = 'si-row';
      row.innerHTML = `
        <button class="si-head">
          <span class="si-ic">${icon(grp.type)}</span>
          <span class="si-name">${escapeHtml(name)}</span>
          <span class="si-meta">${humanSize(f.sizeBytes)} · ${ago(f.remoteModifiedMs)}</span>
          <span class="si-chev">›</span>
        </button>
        <div class="si-detail"></div>`;
      listEl.appendChild(row);
      const head = row.querySelector('.si-head') as HTMLButtonElement;
      const detail = row.querySelector('.si-detail') as HTMLElement;
      head.addEventListener('click', async () => {
        const open = row.classList.toggle('open');
        if (!open || detail.dataset.loaded) return;
        detail.dataset.loaded = '1';
        // Reuse the pre-fetched body for code/tombstone rows.
        if (bodyCache.has(f.path)) { detail.innerHTML = renderFileDetail(bodyCache.get(f.path) ?? null, grp.type); return; }
        detail.innerHTML = '<span class="ss-hint">Reading…</span>';
        try {
          detail.innerHTML = renderFileDetail(await provider.readFile(f.path), grp.type);
        } catch (e) {
          detail.dataset.loaded = '';
          detail.innerHTML = `<span class="ss-hint">Read failed: ${escapeHtml((e as Error).message)}</span>`;
        }
      });
    }
  }
}

/**
 * Scans Drive for stale files and offers to remove them behind a misclick-proof
 * confirm. "Safe" rows (old tombstones, data orphaned by a deleted module) are
 * pre-selected; "review" rows (data with no module — may sync data only) are
 * opt-in via a separate checkbox.
 */
async function runSweepFlow(provider: SyncProvider, container?: HTMLElement): Promise<void> {
  showToast('🧹 Scanning Drive…');
  let scan: SweepScan;
  try { scan = await scanStaleFiles(provider); }
  catch (e) { showToast(`Scan failed: ${(e as Error).message}`); return; }

  if (!scan.safe.length && !scan.review.length) {
    showToast('✓ Nothing to clean up');
    return;
  }

  const totalBytes = (fs: { sizeBytes: number }[]): number => fs.reduce((s, f) => s + (f.sizeBytes || 0), 0);
  const rowHtml = (f: { path: string; reason: string; sizeBytes: number }): string =>
    `<div class="si-kv"><span>${escapeHtml(f.path.replace(/^shell\//, ''))}</span>` +
    `<span>${humanSize(f.sizeBytes)} · ${escapeHtml(f.reason)}</span></div>`;

  const overlay = openOverlay('🧹 Sweep stale files');
  const body = overlay.querySelector('.overlay-body')! as HTMLElement;
  body.innerHTML = `
    ${scan.safe.length ? `
      <div class="si-group">Safe to remove (${scan.safe.length} · ${humanSize(totalBytes(scan.safe))})</div>
      ${scan.safe.map(rowHtml).join('')}` : '<p class="ss-hint">No clearly-stale files found.</p>'}
    ${scan.review.length ? `
      <div class="si-group" style="margin-top:10px">Review (${scan.review.length} · ${humanSize(totalBytes(scan.review))})</div>
      ${scan.review.map(rowHtml).join('')}
      <label class="confirm-ack" style="margin-top:10px">
        <input type="checkbox" class="sw-review">
        <span>Also remove these ${scan.review.length} — they have no module on Drive. Only do this if none of your modules sync <em>data only</em>.</span>
      </label>` : ''}
    <div class="confirm-actions" style="margin-top:18px">
      <button class="cf-cancel">Cancel</button>
      <button class="cf-go"${scan.safe.length ? '' : ' disabled'}>Remove ${scan.safe.length}</button>
    </div>`;

  const go = body.querySelector('.cf-go') as HTMLButtonElement;
  const reviewChk = body.querySelector('.sw-review') as HTMLInputElement | null;
  const recount = (): void => {
    const n = scan.safe.length + (reviewChk?.checked ? scan.review.length : 0);
    go.textContent = `Remove ${n}`;
    go.disabled = n === 0;
  };
  reviewChk?.addEventListener('change', recount);
  body.querySelector('.cf-cancel')!.addEventListener('click', () => overlay.remove());
  go.addEventListener('click', async () => {
    const paths = [
      ...scan.safe.map((f) => f.path),
      ...(reviewChk?.checked ? scan.review.map((f) => f.path) : []),
    ];
    if (!paths.length) return;
    go.disabled = true; go.textContent = 'Removing…';
    const { deleted, failed } = await sweepFiles(provider, paths);
    overlay.remove();
    showToast(failed ? `🧹 Removed ${deleted}, ${failed} failed` : `🧹 Removed ${deleted} file${deleted === 1 ? '' : 's'}`);
    if (container) void renderLiveFiles(container, provider);
  });
}

/** Rolling checkpoint history. Read-only here — recovery actions land in a
 *  later phase. Newest first; #N is the display ordinal (highest = newest). */
async function renderTimeline(container: HTMLElement, provider: SyncProvider): Promise<void> {
  container.innerHTML = '<p class="ss-hint" id="si-status">Loading checkpoints…</p><div id="si-list"></div>';
  const statusEl = container.querySelector('#si-status') as HTMLElement;
  const listEl = container.querySelector('#si-list') as HTMLElement;

  let checkpoints: CheckpointMeta[];
  try { checkpoints = await listCheckpoints(provider); }
  catch (e) { statusEl.textContent = `Failed to load checkpoints: ${(e as Error).message}`; return; }

  if (!checkpoints.length) {
    statusEl.textContent = 'No checkpoints yet.';
    listEl.innerHTML = `<p class="ss-hint">A backup is saved automatically after each sync that changes something — the last ${getCheckpointKeep()} are kept. Sync to create your first one.</p>`;
    return;
  }
  const thisDev = getOrCreateDeviceId();
  statusEl.textContent = `${checkpoints.length} checkpoint${checkpoints.length === 1 ? '' : 's'} · keeping last ${getCheckpointKeep()}`;

  checkpoints.forEach((cp, i) => {
    const n = checkpoints.length - i; // newest gets the highest number
    const dev = cp.deviceId === thisDev ? `${cp.deviceId} (this device)` : cp.deviceId;
    const row = document.createElement('div');
    row.className = 'si-row';
    row.innerHTML = `
      <button class="si-head">
        <span class="si-ic">🕑</span>
        <span class="si-name">#${n} · ${escapeHtml(dev)}</span>
        <span class="si-meta">${humanSize(cp.sizeBytes)} · ${ago(cp.createdAtMs)}</span>
        <span class="si-chev">›</span>
      </button>
      <div class="si-detail"></div>`;
    listEl.appendChild(row);
    const head = row.querySelector('.si-head') as HTMLButtonElement;
    const detail = row.querySelector('.si-detail') as HTMLElement;
    head.addEventListener('click', async () => {
      const open = row.classList.toggle('open');
      if (!open || detail.dataset.loaded) return;
      detail.dataset.loaded = '1';
      detail.innerHTML = '<span class="ss-hint">Reading…</span>';
      try {
        const full = await readCheckpoint(provider, cp.path);
        renderCheckpointDetail(detail, full, container, provider);
      } catch (e) {
        detail.dataset.loaded = '';
        detail.innerHTML = `<span class="ss-hint">Read failed: ${escapeHtml((e as Error).message)}</span>`;
      }
    });
  });
}

/**
 * Renders a checkpoint's contents into `detail` and wires the Phase-3 recovery
 * actions: "Recover" per module + "Recover everything" for the whole snapshot.
 * Each action goes through a misclick-proof confirm before writing-as-newest.
 */
function renderCheckpointDetail(
  detail: HTMLElement,
  cp: Checkpoint | null,
  timelineContainer: HTMLElement,
  provider: SyncProvider,
): void {
  if (!cp || !cp.modules) { detail.innerHTML = '<span class="ss-hint">Empty or unreadable.</span>'; return; }
  const ids = Object.keys(cp.modules).sort();
  const when = escapeHtml(new Date(cp.createdAtMs).toLocaleString());

  detail.innerHTML = `
    <div class="si-kv"><span>Captured</span><span>${when}</span></div>
    <div class="si-kv"><span>Modules</span><span>${ids.length}</span></div>
    <div class="si-group" style="padding:8px 0 4px">In this checkpoint</div>
    <div class="cp-mods"></div>
    <div class="cp-restore-all">
      <button class="cp-recover-all">♻️ Recover all ${ids.length} module${ids.length === 1 ? '' : 's'}</button>
    </div>`;

  const modsEl = detail.querySelector('.cp-mods') as HTMLElement;
  for (const id of ids) {
    const m = cp.modules[id]!;
    const fileKeys = Object.keys(m.files ?? {}).length;
    const dataKeys = Object.keys(m.data ?? {}).length;
    const exists = !!storage.getModule(id);
    const rowEl = document.createElement('div');
    rowEl.className = 'cp-mod';
    rowEl.innerHTML = `
      <span class="cp-mod-name">${escapeHtml(m.icon ?? '🧩')} ${escapeHtml(m.name || id)}</span>
      <span class="cp-mod-meta">${fileKeys} files · ${dataKeys} keys${exists ? '' : ' · <em>not on this device</em>'}</span>
      <button class="cp-recover-one">♻️ Recover</button>`;
    modsEl.appendChild(rowEl);
    rowEl.querySelector('.cp-recover-one')!.addEventListener('click', async () => {
      const ok = await confirmRecover(
        'Recover this module?',
        `<strong>${escapeHtml(m.name || id)}</strong> will be restored from the backup taken on ${when}.` +
          (exists ? ' Its current version is archived to History first, so this can be undone.' : ' It isn’t on this device yet — recovery brings it here.'),
        'Recover module',
      );
      if (!ok) return;
      restoreModuleFromCheckpoint(cp, id);
      await afterRestore(`♻️ Recovered “${m.name || id}”`, timelineContainer, provider);
    });
  }

  detail.querySelector('.cp-recover-all')!.addEventListener('click', async () => {
    const ok = await confirmRecover(
      'Recover the whole backup?',
      `All <strong>${ids.length}</strong> module${ids.length === 1 ? '' : 's'} from the backup taken on ${when} will be restored, ` +
        'overwriting the current versions. Each current version is archived to History first.',
      `Recover all ${ids.length}`,
    );
    if (!ok) return;
    const summary = restoreWholeCheckpoint(cp);
    await afterRestore(`♻️ Recovered ${summary.modulesRestored} module${summary.modulesRestored === 1 ? '' : 's'}`, timelineContainer, provider);
  });
}

/** Refresh the launcher, push the recovery out via a sync, and re-list. */
async function afterRestore(message: string, timelineContainer: HTMLElement, provider: SyncProvider): Promise<void> {
  render();
  showToast(`${message} — syncing…`);
  try { await syncManager.syncNow(); } catch { /* recovery already applied locally */ }
  showToast('✓ Recovery synced');
  void renderTimeline(timelineContainer, provider);
}

/**
 * Blocking, misclick-proof confirm. The destructive button stays disabled
 * until the user ticks an explicit acknowledgement, and it sits apart from
 * Cancel so a stray tap can't trigger it. Resolves true only on a deliberate
 * confirm. `message` may contain trusted HTML built by the caller.
 */
function confirmRecover(title: string, message: string, confirmLabel: string): Promise<boolean> {
  return new Promise((resolve) => {
    const root = document.createElement('div');
    root.className = 'overlay confirm-overlay';
    root.innerHTML = `
      <div class="overlay-card confirm-card">
        <div class="overlay-head"><h2>⚠️ ${escapeHtml(title)}</h2></div>
        <div class="overlay-body">
          <p class="confirm-msg">${message}</p>
          <label class="confirm-ack">
            <input type="checkbox" class="cf-ack">
            <span>I understand this overwrites the current version.</span>
          </label>
          <div class="confirm-actions">
            <button class="cf-cancel">Cancel</button>
            <button class="cf-go" disabled>${escapeHtml(confirmLabel)}</button>
          </div>
        </div>
      </div>`;
    let settled = false;
    const done = (v: boolean): void => { if (settled) return; settled = true; root.remove(); resolve(v); };
    const ack = root.querySelector('.cf-ack') as HTMLInputElement;
    const go = root.querySelector('.cf-go') as HTMLButtonElement;
    ack.addEventListener('change', () => { go.disabled = !ack.checked; });
    root.querySelector('.cf-cancel')!.addEventListener('click', () => done(false));
    go.addEventListener('click', () => { if (ack.checked) done(true); });
    // Click outside or Esc cancels — only the explicit button confirms.
    root.addEventListener('click', (e) => { if (e.target === root) done(false); });
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') { document.removeEventListener('keydown', onKey); done(false); }
    });
    document.body.appendChild(root);
  });
}

// ─── AI profile form (v3 port) ───────────────────────────────────────────────

/**
 * One sheet replaces the old four bare inputs: provider segments, rename
 * support, and live model discovery ("Find models" asks the provider which
 * models the entered key can use; a tappable list because datalists are
 * invisible on most mobile keyboards).
 */
function openProfileForm(existingName: string | null, existing: AiProfile | null, onSaved: () => void): void {
  const isEdit = !!existingName;
  const overlay = openOverlay(isEdit ? 'Edit AI profile' : 'Add AI profile');
  const body = overlay.querySelector('.overlay-body')! as HTMLElement;
  let provider = existing?.provider && AI_PROVIDERS[existing.provider] ? existing.provider : 'gemini';

  const providerSegLabel = (k: string): string => {
    if (k === 'anthropic') return 'Anthropic Claude';
    if (k === 'openai') return 'OpenAI GPT';
    if (k === 'ollama') return 'Ollama (local)';
    return AI_PROVIDERS[k].label.split(' ')[0];
  };
  const providerSeg = Object.keys(AI_PROVIDERS).map((k) =>
    `<button data-prov="${k}" data-active="${k === provider ? '1' : '0'}">${providerSegLabel(k)}</button>`,
  ).join('');

  body.innerHTML = `
    <div class="field">
      <label class="field-label">Provider</label>
      <div class="seg" id="pf-provider">${providerSeg}</div>
    </div>
    <div class="field">
      <label class="field-label">Profile name</label>
      <input class="ss-input" id="pf-name" placeholder="e.g. My Gemini" value="${escapeHtml(existingName ?? '')}"/>
      ${isEdit ? '<p class="ss-hint" style="margin:6px 0 0">You can rename it — modules that mention the old name must be told the new one.</p>' : ''}
    </div>
    <div class="field">
      <label class="field-label" id="pf-keylabel">API key</label>
      <input class="ss-input" id="pf-key" type="password" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Paste your key" value="${escapeHtml(existing?.key ?? '')}"/>
      <p class="ss-hint" id="pf-keyhint" style="margin:6px 0 0"></p>
    </div>
    <div class="field">
      <label class="field-label">Model</label>
      <div style="display:flex;gap:8px">
        <input class="ss-input" id="pf-model" list="pf-models" placeholder="Model id" value="${escapeHtml(existing?.model ?? '')}" style="flex:1;min-width:0"/>
        <button class="mini-btn" id="pf-fetch" style="flex-shrink:0">Find models</button>
      </div>
      <datalist id="pf-models"></datalist>
      <p class="ss-hint" id="pf-modelhint" style="margin:6px 0 0"></p>
      <div id="pf-modellist" style="max-height:190px;overflow-y:auto;margin-top:6px;display:flex;flex-direction:column;gap:4px"></div>
    </div>
    <div class="ss-actions" style="margin-top:6px">
      <button id="pf-save" style="flex:1">${isEdit ? 'Save' : 'Add profile'}</button>
    </div>
  `;

  const nameInp   = body.querySelector('#pf-name')      as HTMLInputElement;
  const keyInp    = body.querySelector('#pf-key')       as HTMLInputElement;
  const keyLabel  = body.querySelector('#pf-keylabel')  as HTMLElement;
  const modelInp  = body.querySelector('#pf-model')     as HTMLInputElement;
  const keyHint   = body.querySelector('#pf-keyhint')   as HTMLElement;
  const datalist  = body.querySelector('#pf-models')    as HTMLElement;
  const fetchBtn  = body.querySelector('#pf-fetch')     as HTMLButtonElement;
  const modelHint = body.querySelector('#pf-modelhint') as HTMLElement;
  const modelList = body.querySelector('#pf-modellist') as HTMLElement;

  const renderModelOptions = (models: string[]): void => {
    const sel = modelInp.value.trim();
    modelList.innerHTML = models.map((m) => `
      <div data-m="${escapeHtml(m)}" style="padding:10px 12px;border-radius:10px;cursor:pointer;font-size:13px;
           ${m === sel ? 'background:rgba(74,222,128,0.14);color:#4ade80;font-weight:600' : 'background:rgba(127,127,127,0.12)'}">${escapeHtml(m)}</div>`).join('');
    modelList.querySelectorAll('[data-m]').forEach((el) => el.addEventListener('click', () => {
      modelInp.value = (el as HTMLElement).dataset.m!;
      renderModelOptions(models);
    }));
  };

  let fetching = false;
  const findModels = async (): Promise<void> => {
    if (fetching) return;
    // Ollama uses the same field for a base URL — and an empty value means
    // "the default localhost endpoint", so it's a valid fetch.
    const key = keyInp.value.trim() || (provider === 'ollama' ? OLLAMA_DEFAULT_BASE_URL : '');
    if (!key) { modelHint.textContent = 'Paste your API key above first, then tap “Find models”.'; return; }
    fetching = true;
    fetchBtn.textContent = '…';
    modelHint.textContent = provider === 'ollama'
      ? `Asking Ollama which models you have installed locally…`
      : `Asking ${AI_PROVIDERS[provider].label} which models your key can use…`;
    const { models, warn } = await fetchModelsForProvider(provider, key);
    fetching = false;
    fetchBtn.textContent = 'Find models';
    modelHint.textContent = warn
      ? `Couldn't fetch the live list (${warn}) — showing built-in suggestions.`
      : `${models.length} models available — tap one to select it.`;
    datalist.innerHTML = models.map((m) => `<option value="${escapeHtml(m)}"></option>`).join('');
    if (!modelInp.value.trim() && models.length) modelInp.value = models[0];
    renderModelOptions(models);
  };

  const refreshForProvider = (): void => {
    const meta = AI_PROVIDERS[provider];
    keyHint.textContent = meta.keyHint;
    if (provider === 'ollama') {
      keyLabel.textContent = 'Base URL';
      keyInp.type = 'text';
      keyInp.placeholder = OLLAMA_DEFAULT_BASE_URL;
    } else {
      keyLabel.textContent = 'API key';
      keyInp.type = 'password';
      keyInp.placeholder = 'Paste your key';
    }
    datalist.innerHTML = meta.models.map((m) => `<option value="${escapeHtml(m)}"></option>`).join('');
    if (!modelInp.value) modelInp.value = meta.defaultModel;
    if (!isEdit && (!nameInp.value || Object.values(AI_PROVIDERS).some((p) => p.label === nameInp.value))) {
      nameInp.value = meta.label;
    }
    modelHint.textContent = provider === 'ollama'
      ? 'Built-in suggestions — tap “Find models” to list the models installed on your Ollama service.'
      : 'Built-in suggestions — tap “Find models” to fetch the full list for your key.';
    renderModelOptions(meta.models);
  };
  refreshForProvider();
  // Editing an existing profile: the key is already there, fetch right away.
  // (Ollama also auto-fetches because an empty field still maps to the default URL.)
  if (keyInp.value.trim() || provider === 'ollama') void findModels();

  body.querySelectorAll('#pf-provider button').forEach((btn) => {
    btn.addEventListener('click', () => {
      provider = (btn as HTMLElement).dataset.prov!;
      body.querySelectorAll('#pf-provider button').forEach((b) =>
        (b as HTMLElement).dataset.active = b === btn ? '1' : '0');
      // Reset model to the new provider's default when switching.
      modelInp.value = AI_PROVIDERS[provider].defaultModel;
      refreshForProvider();
    });
  });
  fetchBtn.addEventListener('click', () => void findModels());
  keyInp.addEventListener('change', () => { if (keyInp.value.trim()) void findModels(); });

  body.querySelector('#pf-save')!.addEventListener('click', () => {
    const name = nameInp.value.trim();
    const rawKey = keyInp.value.trim();
    // Ollama: empty field means "use the default localhost URL" — record that
    // explicitly so the saved profile is self-describing.
    const key = provider === 'ollama' ? (rawKey || OLLAMA_DEFAULT_BASE_URL) : rawKey;
    const model = modelInp.value.trim() || AI_PROVIDERS[provider].defaultModel;
    if (!name) { showToast('Give the profile a name'); return; }
    if (!key)  { showToast(provider === 'ollama' ? 'A base URL is required' : 'An API key is required'); return; }
    // Renaming = save under the new name, then drop the old entry.
    if (isEdit && name !== existingName) deleteAiProfile(existingName!);
    setAiProfile(name, key, model, provider);
    showToast(isEdit ? 'Profile saved' : `Added ${name}`);
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
    onSaved();
  });
}

// ─── Per-module Storage & Sync menu ──────────────────────────────────────────

function showModuleMenu(id: string, rec: ModuleRecord): void {
  const overlay = openOverlay(rec.name);
  overlay.classList.add('module-overlay');
  const body = overlay.querySelector('.overlay-body')!;

  const isBuiltin = BUILTIN_IDS.includes(id);

  body.innerHTML = `
    <section class="settings-section">
      <h3>☁️ Sync across my devices</h3>
      <p class="ss-hint">Keep this module's data and code the same on every device. Fine-grained data/code control lives under Advanced.</p>
      <label class="mod-switch"><input type="checkbox" id="sync-all-chk"/> Sync this module</label>
    </section>
    <section class="settings-section">
      <h3>🔄 Keep fresh in background</h3>
      <p class="ss-hint">Loads this module invisibly shortly after Shell opens, so the data it shares with other modules stays current without opening it.</p>
      <label class="mod-switch"><input type="checkbox" id="bg-refresh-chk"/> Refresh on launch</label>
    </section>
    <section class="settings-section">
      <h3>✏️ Name &amp; icon</h3>
      <div style="display:flex;gap:8px;margin-bottom:8px">
        <input class="ss-input" id="mod-icon" value="${escapeHtml(rec.icon ?? '🧩')}"
          maxlength="4" aria-label="Icon emoji" style="width:62px;text-align:center;font-size:20px;flex-shrink:0"/>
        <input class="ss-input" id="mod-name" value="${escapeHtml(rec.name)}" aria-label="Module name" style="flex:1;min-width:0"/>
        <button class="mini-btn" id="mod-name-save" style="flex-shrink:0">Save</button>
      </div>
      <div id="icon-suggest" style="display:flex;gap:4px;flex-wrap:wrap"></div>
    </section>
    <section class="settings-section">
      <h3>📂 Section</h3>
      <p class="ss-hint">Group this module on the home screen.</p>
      <div id="mod-sections" style="display:flex;flex-wrap:wrap;gap:6px"></div>
    </section>
    <details class="settings-section">
      <summary>About this module</summary>
      <div class="ss-row"><span>ID</span><span>${escapeHtml(id)}</span></div>
      <div class="ss-row"><span>Type</span><span>${isBuiltin ? 'Built-in' : 'Added by you'}</span></div>
      <div class="ss-row" style="border-bottom:0"><span>Updated</span><span>${rec.lastModifiedMs ? new Date(rec.lastModifiedMs).toLocaleString() : '—'}</span></div>
    </details>
    <details class="settings-section">
      <summary>Advanced</summary>
      <h4>💾 Data sync</h4>
      <p class="ss-hint">Sync this module's saved data (entries, settings) across devices.</p>
      <div class="toggle-row">
        <button class="toggle" data-mode="local_only" data-target="data">📱 Local only</button>
        <button class="toggle" data-mode="sync" data-target="data">☁️ Sync</button>
      </div>
      <h4>📝 Code sync</h4>
      <p class="ss-hint">Sync the module's HTML source itself — changes on one device reach all.</p>
      <div class="toggle-row">
        <button class="toggle" data-mode="local_only" data-target="code">📱 Local only</button>
        <button class="toggle" data-mode="sync" data-target="code">☁️ Sync</button>
      </div>
      <h4>🐙 GitHub source</h4>
      <p class="ss-hint">Paste the GitHub link of the module's <b>index.html</b> file — the app figures out the repo, branch, and path on its own. Private repos need a token (set once in ⚙️ Settings → GitHub).</p>
      <div id="gh-status" class="ss-hint" style="margin-bottom:6px"></div>
      <input class="ss-input" id="gh-url" placeholder="https://github.com/owner/repo/blob/main/index.html" style="margin-bottom:8px"/>
      <div class="ss-actions" style="gap:8px">
        <button id="gh-save" class="mini-btn">Save</button>
        <button id="gh-pull">⬇️ Save & pull latest</button>
        <button id="gh-clear" class="mini-btn" hidden>Disconnect</button>
      </div>
      <h4>🕘 History</h4>
      <p class="ss-hint">Replace the code from a file or by pasting it. The current version is kept here first.</p>
      <div class="ss-actions" style="margin-bottom:8px">
        <button id="rev-upload-btn">⬆️ Upload file</button>
        <button id="rev-paste-btn" class="mini-btn">📋 Paste code</button>
      </div>
      <div id="rev-paste-box" hidden style="margin-bottom:8px">
        <textarea id="rev-paste-text" class="ss-input"
          style="min-height:140px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;line-height:1.4"
          placeholder="Paste raw HTML, or a Shell .json export…"></textarea>
        <div class="ss-actions" style="margin-top:8px">
          <button id="rev-paste-apply">✓ Apply</button>
          <button id="rev-paste-cancel" class="mini-btn">Cancel</button>
        </div>
      </div>
      <div id="rev-list"></div>
      <h4>📤 Export</h4>
      <p class="ss-hint">Download a copy. Choose what to include.</p>
      <div class="ss-actions">
        <button id="export-module-btn">📥 Module &amp; data</button>
        <button id="export-code-btn" class="mini-btn">📄 Code only</button>
        <button id="export-data-btn" class="mini-btn">🗂️ Data only</button>
      </div>
      <h4>📥 Import data</h4>
      <p class="ss-hint">Restore saved data from a "Data only" .json file. Existing keys with the same name are overwritten.</p>
      <div class="ss-actions"><button id="import-data-btn" class="mini-btn">🗂️ Import data file</button></div>
    </details>
    ${isBuiltin ? '' : `
    <section class="settings-section">
      <h3>🗑️ Delete</h3>
      <p class="ss-hint">Removes the module locally. If code-sync was on, it'll be re-discovered next sync — disable code-sync first to delete permanently.</p>
      <div class="ss-actions"><button id="del-module-btn" style="background:#a33;color:#fff">🗑️ Delete this module</button></div>
    </section>`}
  `;

  // Name & icon — written into the record AND files["module.json"] so the
  // change rides code sync to Android (and back).
  const iconInp = body.querySelector('#mod-icon') as HTMLInputElement;
  const nameInp = body.querySelector('#mod-name') as HTMLInputElement;
  const suggestWrap = body.querySelector('#icon-suggest') as HTMLElement;
  suggestWrap.innerHTML = ['🧩','📦','📋','📈','🏃','💧','🍽️','💰','🎵','📚','🛠️','🌦️']
    .map((e) => `<button class="mini-btn" data-ico="${e}" style="font-size:16px;padding:5px 9px">${e}</button>`).join('');
  suggestWrap.querySelectorAll('[data-ico]').forEach((b) =>
    b.addEventListener('click', () => { iconInp.value = (b as HTMLElement).dataset.ico!; }));
  body.querySelector('#mod-name-save')!.addEventListener('click', () => {
    const newName = nameInp.value.trim();
    const newIcon = iconInp.value.trim() || '🧩';
    if (!newName) { showToast('Name can\'t be empty'); return; }
    const cur = storage.getModule(id) as ModuleRecord | undefined;
    if (!cur) return;
    let section = 'General';
    try {
      const prev = JSON.parse(cur.files?.['module.json'] ?? '{}') as { section?: string };
      if (typeof prev.section === 'string') section = prev.section;
    } catch { /* keep default */ }
    storage.putModule(id, {
      ...cur,
      name: newName,
      icon: newIcon,
      files: {
        ...cur.files,
        'index.html': cur.htmlSource,
        'module.json': JSON.stringify({ label: newName, icon: newIcon, section }),
      },
      // Bump so code sync pushes the change (whole-module LWW).
      lastModifiedMs: Date.now(),
    });
    showToast('✓ Saved');
    render();
  });

  // Section picker — chips for each existing section + "New…". Picking one
  // writes the module's section (rides code sync) and re-renders the home.
  const secWrap = body.querySelector('#mod-sections') as HTMLElement;
  const renderSectionChips = (): void => {
    const current = moduleSection(storage.getModule(id) as ModuleRecord);
    const chips = effectiveSections().map((s) =>
      `<button class="mini-btn" data-sec="${escapeHtml(s)}"${s === current
        ? ' style="background:var(--accent);color:var(--accent-text);border-color:var(--accent)"' : ''}>${escapeHtml(s)}</button>`).join('');
    secWrap.innerHTML = chips + `<button class="mini-btn" data-sec-new="1">＋ New…</button>`;
    secWrap.querySelectorAll('[data-sec]').forEach((b) =>
      b.addEventListener('click', () => {
        setModuleSection(id, (b as HTMLElement).dataset.sec!);
        renderSectionChips();
        render();
      }));
    secWrap.querySelector('[data-sec-new]')!.addEventListener('click', () =>
      promptText('New section', '', (v) => {
        addSectionNamed(v);
        setModuleSection(id, v.trim());
        renderSectionChips();
        render();
      }));
  };
  renderSectionChips();

  const bgChk = body.querySelector('#bg-refresh-chk') as HTMLInputElement;
  bgChk.checked = !!(storage.getModule(id) as ModuleRecord | undefined)?.backgroundRefresh;
  bgChk.addEventListener('change', () => {
    const cur = storage.getModule(id) as ModuleRecord | undefined;
    if (!cur) return;
    storage.putModule(id, { ...cur, backgroundRefresh: bgChk.checked });
    showToast(bgChk.checked ? '🔄 Will refresh on launch' : 'Background refresh off');
  });

  const renderRevisions = (): void => {
    const list = document.getElementById('rev-list');
    if (!list) return;
    const cur = storage.getModule(id) as ModuleRecord | undefined;
    const revs = Object.keys(cur?.files ?? {})
      .map((p) => p.match(/^revisions\/(index_(\d+)\.html)$/))
      .filter((m): m is RegExpMatchArray => m !== null)
      .sort((a, b) => Number(b[2]) - Number(a[2]));
    if (!revs.length) {
      list.innerHTML = '<p class="ss-hint">No earlier versions yet. When the code changes (AI update or sync), the old version is kept here.</p>';
      return;
    }
    list.innerHTML = revs.map((m) => `
      <div class="ss-row">
        <span>${new Date(Number(m[2])).toLocaleString()}</span>
        <button data-restore="${m[1]}" style="background:transparent;border:0;color:var(--accent,#c8f060);cursor:pointer;font:inherit;font-size:13px;font-weight:600">↩ Restore</button>
      </div>`).join('');
    list.querySelectorAll('[data-restore]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const revName = (btn as HTMLElement).dataset.restore!;
        const rec2 = storage.getModule(id) as ModuleRecord | undefined;
        const src = rec2?.files?.[`revisions/${revName}`];
        if (!rec2 || !src) return;
        if (!confirm('Restore this earlier version? The current version is kept in History.')) return;
        const files: Record<string, string> = { ...rec2.files, 'index.html': src };
        if (rec2.htmlSource && rec2.htmlSource !== src) {
          files[`revisions/index_${rec2.lastModifiedMs || Date.now()}.html`] = rec2.htmlSource;
        }
        storage.putModule(id, { ...rec2, htmlSource: src, files, lastModifiedMs: Date.now() });
        renderRevisions();
        showToast('↩ Earlier version restored');
      });
    });
  };
  renderRevisions();
  body.querySelector('#rev-upload-btn')?.addEventListener('click', () => uploadRevisionFromFile(id, renderRevisions));
  const pasteBox = body.querySelector('#rev-paste-box') as HTMLElement;
  const pasteText = body.querySelector('#rev-paste-text') as HTMLTextAreaElement;
  body.querySelector('#rev-paste-btn')?.addEventListener('click', () => {
    const opening = pasteBox.hidden;
    pasteBox.hidden = !opening;
    if (opening) pasteText.focus();
  });
  body.querySelector('#rev-paste-cancel')?.addEventListener('click', () => {
    pasteBox.hidden = true;
    pasteText.value = '';
  });
  body.querySelector('#rev-paste-apply')?.addEventListener('click', () => {
    const text = pasteText.value.trim();
    if (!text) { showToast('Nothing to apply'); return; }
    applyRevisionFromText(id, text, () => {
      pasteText.value = '';
      pasteBox.hidden = true;
      renderRevisions();
    });
  });

  // GitHub source — single URL paste; the app parses owner/repo/branch/path.
  const ghUrl = body.querySelector('#gh-url') as HTMLInputElement;
  const ghStatus = body.querySelector('#gh-status') as HTMLElement;
  const ghClear = body.querySelector('#gh-clear') as HTMLButtonElement;
  const renderGhStatus = (): void => {
    const src = getGithubSource(id);
    if (src) {
      ghStatus.innerHTML = `🔗 Connected — pulls <b>${escapeHtml(src.path)}</b> from <b>${escapeHtml(src.repo)}</b> on <b>${escapeHtml(src.branch)}</b>`;
      ghClear.hidden = false;
    } else {
      ghStatus.innerHTML = '<em>Not connected to a GitHub source yet.</em>';
      ghClear.hidden = true;
    }
  };
  renderGhStatus();
  const saveGhSource = (): boolean => {
    const raw = ghUrl.value.trim();
    if (!raw) { showToast('Paste a github.com link to the .html file'); return false; }
    const parsed = parseGithubFileUrl(raw);
    if (!parsed) { showToast('That link doesn\'t look like a GitHub file URL — paste the link to the .html file'); return false; }
    setGithubSource(id, parsed);
    ghUrl.value = '';
    renderGhStatus();
    return true;
  };
  body.querySelector('#gh-save')!.addEventListener('click', () => { if (saveGhSource()) showToast('🐙 Source saved'); });
  body.querySelector('#gh-pull')!.addEventListener('click', () => {
    // If they pasted something, save it first; otherwise pull from the saved source.
    if (ghUrl.value.trim()) { if (!saveGhSource()) return; }
    if (!getGithubSource(id)) { showToast('Paste a GitHub link first'); return; }
    void pullFromGithub(id, renderRevisions);
  });
  ghClear.addEventListener('click', () => {
    if (!confirm('Disconnect this module from its GitHub source? Local code stays untouched.')) return;
    setGithubSource(id, null);
    renderGhStatus();
    showToast('🔌 Disconnected');
  });

  const updateToggles = (): void => {
    const cur = storage.getModule(id) as ModuleRecord | undefined;
    if (!cur) return;
    const data = cur.dataSyncMode ?? 'local_only';
    const code = cur.codeSyncMode ?? 'local_only';
    body.querySelectorAll('.toggle[data-target="data"]').forEach((b) => {
      const btn = b as HTMLButtonElement;
      btn.dataset.active = btn.dataset.mode === data ? 'true' : 'false';
    });
    body.querySelectorAll('.toggle[data-target="code"]').forEach((b) => {
      const btn = b as HTMLButtonElement;
      btn.dataset.active = btn.dataset.mode === code ? 'true' : 'false';
    });
    // Keep the primary "Sync across my devices" switch in step with the split toggles.
    const sc = body.querySelector('#sync-all-chk') as HTMLInputElement | null;
    if (sc) {
      sc.checked = data === 'sync' && code === 'sync';
      sc.indeterminate = (data === 'sync') !== (code === 'sync');
    }
  };

  body.querySelectorAll('.toggle').forEach((b) => {
    b.addEventListener('click', () => {
      const el = b as HTMLElement;
      const mode = el.dataset.mode as 'local_only' | 'sync';
      const target = el.dataset.target as 'data' | 'code';
      const cur = storage.getModule(id) as ModuleRecord | undefined;
      if (!cur) return;
      const prevMode = target === 'data' ? cur.dataSyncMode : cur.codeSyncMode;
      const patch = target === 'data'
        ? { dataSyncMode: mode }
        : { codeSyncMode: mode };
      storage.putModule(id, { ...cur, ...patch });
      updateToggles();
      const label = target === 'data' ? 'Data' : 'Code';
      showToast(mode === 'sync' ? `☁️ ${label} sync on` : `📱 ${label} local only`);

      // Drop Drive snapshot when transitioning sync→local_only, so a stale
      // copy doesn't propagate back via auto-discovery on the next sync.
      if (prevMode === 'sync' && mode !== 'sync') {
        void syncManager.cleanupModuleFromDrive(
          id,
          /* deleteData */ target === 'data',
          /* deleteCode */ target === 'code',
        );
      }
    });
  });

  // Primary switch — flips both data + code together (the split lives in Advanced).
  const syncAllChk = body.querySelector('#sync-all-chk') as HTMLInputElement;
  syncAllChk.addEventListener('change', () => {
    const cur = storage.getModule(id) as ModuleRecord | undefined;
    if (!cur) return;
    const mode = syncAllChk.checked ? 'sync' : 'local_only';
    const prevData = cur.dataSyncMode ?? 'local_only';
    const prevCode = cur.codeSyncMode ?? 'local_only';
    storage.putModule(id, { ...cur, dataSyncMode: mode, codeSyncMode: mode });
    updateToggles();
    showToast(mode === 'sync' ? '☁️ Syncing across devices' : '📱 Now on this device only');
    // Turning sync off → drop the Drive snapshots so they don't re-propagate.
    if (mode !== 'sync' && (prevData === 'sync' || prevCode === 'sync')) {
      void syncManager.cleanupModuleFromDrive(id, prevData === 'sync', prevCode === 'sync');
    }
  });

  document.getElementById('export-module-btn')?.addEventListener('click', () => {
    void exportModuleBundle(id);
  });
  document.getElementById('export-code-btn')?.addEventListener('click', () => exportModuleCodeOnly(id));
  document.getElementById('export-data-btn')?.addEventListener('click', () => exportModuleDataOnly(id));
  document.getElementById('import-data-btn')?.addEventListener('click', () => importModuleDataFromFile(id));

  document.getElementById('del-module-btn')?.addEventListener('click', () => {
    if (!confirm(`Delete "${rec.name}"? If it was syncing, it will be deleted on your other devices too.`)) return;
    const wasDataSynced = rec.dataSyncMode === 'sync';
    const wasCodeSynced = rec.codeSyncMode === 'sync';
    storage.removeModule(id);
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
    render();
    // Real deletion → tombstone (explicit delete signal for other devices),
    // unlike the sync-off toggle which just removes the Drive files.
    if (wasDataSynced || wasCodeSynced) {
      void syncManager.markModuleDeletedOnDrive(id, wasCodeSynced, wasDataSynced);
    }
    showToast(`🗑️ ${rec.name} deleted`);
  });

  updateToggles();
}

// ─── Module export (download a module + its data as one file) ─────────────────

/** Bundle format version — bump if the envelope shape changes. */
const EXPORT_FORMAT = 1;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(blob);
  });
}

/**
 * Gathers the module record, all its KV data, and any stored binaries into a
 * single JSON file and triggers a browser download. The desktop equivalent of
 * Android's "export module data to folder".
 */
async function exportModuleBundle(id: string): Promise<void> {
  const rec = storage.getModule(id) as ModuleRecord | undefined;
  if (!rec) { showToast('Module not found'); return; }

  const data: Record<string, string> = {};
  for (const key of storage.listKeysForModule(id)) {
    data[key] = storage.get({ moduleId: id, key });
  }

  const binary: Record<string, string> = {};
  try {
    for (const name of await storage.listBinaryForModule(id)) {
      const blob = await storage.getBinary({ moduleId: id, key: name });
      if (blob) binary[name] = await blobToDataUrl(blob);
    }
  } catch (err) {
    console.warn('[launcher] binary export skipped', err);
  }

  const bundle = {
    shellExport: EXPORT_FORMAT,
    exportedAt: Date.now(),
    module: {
      id: rec.id ?? id,
      name: rec.name,
      icon: rec.icon ?? null,
      htmlSource: rec.htmlSource,
      files: rec.files ?? {},
      dataSyncMode: rec.dataSyncMode ?? 'local_only',
      codeSyncMode: rec.codeSyncMode ?? 'local_only',
      backgroundRefresh: !!rec.backgroundRefresh,
    },
    data,
    binary,
  };

  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shell-${id}-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  showToast(`💾 Exported ${rec.name || id}`);
}

/** Saves just the module's index.html as a .html file (no data, no manifest). */
function exportModuleCodeOnly(id: string): void {
  const rec = storage.getModule(id) as ModuleRecord | undefined;
  if (!rec || !rec.htmlSource) { showToast('No code to export'); return; }
  triggerDownload(rec.htmlSource, `${id}.html`, 'text/html');
  showToast(`📄 Exported code for ${rec.name || id}`);
}

/** Saves just the module's saved data as a small .json file (no code). */
function exportModuleDataOnly(id: string): void {
  const rec = storage.getModule(id) as ModuleRecord | undefined;
  if (!rec) { showToast('Module not found'); return; }
  const data: Record<string, string> = {};
  for (const key of storage.listKeysForModule(id)) {
    data[key] = storage.get({ moduleId: id, key });
  }
  if (!Object.keys(data).length) { showToast('No saved data to export'); return; }
  const bundle = { shellExportKind: 'data', moduleId: id, exportedAt: Date.now(), data };
  triggerDownload(JSON.stringify(bundle, null, 2),
    `shell-${id}-data-${new Date().toISOString().slice(0, 10)}.json`, 'application/json');
  showToast(`🗂️ Exported data for ${rec.name || id}`);
}

/** Loads a "Data only" .json file and merges its entries into this module's storage. */
function importModuleDataFromFile(id: string): void {
  const rec = storage.getModule(id) as ModuleRecord | undefined;
  if (!rec) { showToast('Module not found'); return; }
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result)) as Record<string, unknown>;
        const data = (obj.data ?? obj) as Record<string, unknown>;
        if (!data || typeof data !== 'object') { showToast('No data found in file'); return; }
        const entries = Object.entries(data).filter(([k]) => typeof k === 'string' && !k.startsWith('__'));
        if (!entries.length) { showToast('No data entries to import'); return; }
        if (!confirm(`Import ${entries.length} data entr${entries.length === 1 ? 'y' : 'ies'} into "${rec.name || id}"? Existing keys with the same name will be overwritten.`)) return;
        for (const [k, v] of entries) storage.set({ moduleId: id, key: k }, String(v));
        showToast(`✓ Imported ${entries.length} entries`);
      } catch (e) {
        showToast(`Import failed: ${(e as Error).message}`);
      }
    };
    reader.readAsText(file);
  });
  input.click();
}

function triggerDownload(content: string, name: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Applies pasted text as a new revision — same flow as uploadRevisionFromFile
 * but skips the <input type=file> step. Accepts raw HTML or a Shell .json bundle
 * (full export or the bare {index.html, …} files map).
 */
function applyRevisionFromText(id: string, text: string, onDone: () => void): void {
  void (async () => {
    try {
      const rec = storage.getModule(id) as ModuleRecord | undefined;
      if (!rec) return;
      let newHtml = '';
      let extraFiles: Record<string, string> = {};
      const looksJson = text.trimStart().startsWith('{');
      if (looksJson) {
        const obj = JSON.parse(text) as Record<string, unknown>;
        const m = obj.module as Record<string, unknown> | undefined;
        if (m && typeof m === 'object') {
          extraFiles = (m.files as Record<string, string>) ?? {};
          newHtml = (m.htmlSource as string) || extraFiles['index.html'] || '';
        } else if (typeof obj['index.html'] === 'string') {
          extraFiles = obj as Record<string, string>;
          newHtml = obj['index.html'] as string;
        } else { showToast('Unrecognised JSON — expected a Shell export'); return; }
      } else {
        newHtml = text;
      }
      if (!newHtml.trim()) { showToast('No index.html found in pasted code'); return; }
      const files: Record<string, string> = { ...rec.files, ...extraFiles, 'index.html': newHtml };
      if (rec.htmlSource && rec.htmlSource !== newHtml) {
        const isPlaceholder = rec.htmlSource.length < 200 && rec.htmlSource.includes('<p>');
        if (!isPlaceholder) files[`revisions/index_${rec.lastModifiedMs || Date.now()}.html`] = rec.htmlSource;
      }
      storage.putModule(id, { ...rec, htmlSource: newHtml, files, lastModifiedMs: Date.now() });
      showToast('✓ New revision applied from paste');
      onDone();
    } catch (e) {
      showToast(`Paste failed: ${(e as Error).message}`);
    }
  })();
}

// ─── Import from file ─────────────────────────────────────────────────────────

function dataUrlToBlob(dataUrl: string): Blob {
  const [head, b64] = dataUrl.split(',');
  const mime = head.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream';
  const bin = atob(b64 ?? '');
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/** Slugifies a label into a valid module id, then uniquifies against existing ones. */
function uniqueModuleId(base: string): string {
  const slug = base.toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-')
    .replace(/^-|-$/g, '').slice(0, 40) || 'module';
  if (!storage.getModule(slug)) return slug;
  for (let n = 2; n < 1000; n++) {
    const cand = `${slug}-${n}`.slice(0, 40);
    if (!storage.getModule(cand)) return cand;
  }
  return `${slug}-${Date.now()}`.slice(0, 40);
}

/**
 * Opens a file picker and installs a module from either a single .html file or
 * a Shell .json export bundle (the format exportModuleBundle writes — code,
 * data and binaries). The desktop counterpart to "Upload from file" — lets you
 * install a heavy hand-coded module without pasting into a textarea.
 */
function importModuleFromFile(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.html,.htm,.json,text/html,application/json';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        const text = await file.text();
        const looksJson = file.name.toLowerCase().endsWith('.json') || text.trimStart().startsWith('{');
        if (looksJson) {
          await installBundleText(text, file.name);
        } else {
          // Single HTML file — prefill the create form so the user confirms id/name.
          showCreateModule({ html: text, name: file.name.replace(/\.html?$/i, '') });
        }
      } catch (e) {
        showToast(`Import failed: ${(e as Error).message}`);
      }
    })();
  });
  input.click();
}

/**
 * Replaces an existing module's code from an uploaded file (.html, or a Shell
 * .json export / files map), archiving the current version into History first.
 * The desktop/mobile counterpart to Android's "Upload a file" in the revision
 * uploader.
 */
function uploadRevisionFromFile(id: string, onDone: () => void): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.html,.htm,.json,text/html,application/json';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        const rec = storage.getModule(id) as ModuleRecord | undefined;
        if (!rec) return;
        const text = await file.text();
        let newHtml = '';
        let extraFiles: Record<string, string> = {};
        const looksJson = file.name.toLowerCase().endsWith('.json') || text.trimStart().startsWith('{');
        if (looksJson) {
          const obj = JSON.parse(text) as Record<string, unknown>;
          const m = obj.module as Record<string, unknown> | undefined;
          if (m && typeof m === 'object') {
            extraFiles = (m.files as Record<string, string>) ?? {};
            newHtml = (m.htmlSource as string) || extraFiles['index.html'] || '';
          } else if (typeof obj['index.html'] === 'string') {
            extraFiles = obj as Record<string, string>;
            newHtml = obj['index.html'] as string;
          } else { showToast('Unrecognised file — expected a Shell export or HTML'); return; }
        } else {
          newHtml = text;
        }
        if (!newHtml.trim()) { showToast('No index.html found in that file'); return; }
        const files: Record<string, string> = { ...rec.files, ...extraFiles, 'index.html': newHtml };
        if (rec.htmlSource && rec.htmlSource !== newHtml) {
          const isPlaceholder = rec.htmlSource.length < 200 && rec.htmlSource.includes('<p>');
          if (!isPlaceholder) files[`revisions/index_${rec.lastModifiedMs || Date.now()}.html`] = rec.htmlSource;
        }
        storage.putModule(id, { ...rec, htmlSource: newHtml, files, lastModifiedMs: Date.now() });
        showToast('✓ New revision applied');
        onDone();
      } catch (e) {
        showToast(`Upload failed: ${(e as Error).message}`);
      }
    })();
  });
  input.click();
}

/** Installs a Shell export bundle (or a bare {index.html,…} files map) from JSON text. */
async function installBundleText(text: string, fileName: string): Promise<void> {
  let parsed: unknown;
  try { parsed = JSON.parse(text); } catch { showToast('Not valid JSON'); return; }
  const obj = parsed as Record<string, unknown>;

  // Shape A: a full Shell export bundle { module:{…}, data, binary }.
  const m = obj.module as Record<string, unknown> | undefined;
  if (m && typeof m === 'object') {
    const html = (m.htmlSource as string) || ((m.files as Record<string, string>)?.['index.html']) || '';
    if (!html) { showToast('Bundle has no index.html'); return; }
    const id = uniqueModuleId(String(m.id || m.name || 'module'));
    const name = String(m.name || id);
    storage.putModule(id, {
      id, name, icon: (m.icon as string) ?? undefined,
      htmlSource: html,
      files: { ...((m.files as Record<string, string>) ?? {}), 'index.html': html },
      lastModifiedMs: Date.now(), lastDataSyncMs: 0, lastCodeSyncMs: 0,
      dataSyncMode: m.dataSyncMode === 'sync' ? 'sync' : 'local_only',
      codeSyncMode: m.codeSyncMode === 'sync' ? 'sync' : 'local_only',
      backgroundRefresh: !!m.backgroundRefresh,
    });
    const data = obj.data as Record<string, string> | undefined;
    if (data && typeof data === 'object') {
      for (const [k, v] of Object.entries(data)) storage.set({ moduleId: id, key: k }, String(v));
    }
    const binary = obj.binary as Record<string, string> | undefined;
    if (binary && typeof binary === 'object') {
      for (const [bname, durl] of Object.entries(binary)) {
        try { await storage.putBinary({ moduleId: id, key: bname }, dataUrlToBlob(String(durl))); }
        catch (e) { console.warn('[launcher] binary import skipped', bname, e); }
      }
    }
    render();
    showToast(`✓ Imported “${name}”`);
    return;
  }

  // Shape B: a bare files map { "index.html": "…", … } — confirm id/name.
  if (typeof obj['index.html'] === 'string') {
    showCreateModule({
      html: obj['index.html'] as string,
      name: fileName.replace(/\.json$/i, ''),
      files: obj as Record<string, string>,
    });
    return;
  }

  showToast('Unrecognised file — expected a Shell export or an HTML module');
}

// ─── GitHub source (pull module code from a repo) ─────────────────────────────

interface GithubSource { repo: string; branch: string; path: string; }

function getGithubPat(): string { return localStorage.getItem('shell.gh.pat') ?? ''; }
function setGithubPat(v: string): void {
  const t = v.trim();
  if (t) localStorage.setItem('shell.gh.pat', t); else localStorage.removeItem('shell.gh.pat');
}
function getGithubSource(id: string): GithubSource | null {
  try { return JSON.parse(localStorage.getItem(`shell.gh.${id}`) ?? 'null') as GithubSource | null; }
  catch { return null; }
}
function setGithubSource(id: string, src: GithubSource | null): void {
  if (src) localStorage.setItem(`shell.gh.${id}`, JSON.stringify(src));
  else localStorage.removeItem(`shell.gh.${id}`);
}

/** Accepts "owner/repo" or a github.com URL → {owner, repo}. */
function parseRepo(input: string): { owner: string; repo: string } | null {
  const s = input.trim()
    .replace(/^https?:\/\/github\.com\//i, '')
    .replace(/\.git$/, '')
    .replace(/\/+$/, '');
  const m = s.match(/^([^/\s]+)\/([^/\s]+)/);
  return m ? { owner: m[1]!, repo: m[2]! } : null;
}

/**
 * Parses a GitHub file link into the {repo, branch, path} the source store wants.
 * Accepts:
 *   - https://github.com/owner/repo/blob/<branch>/<path>
 *   - https://github.com/owner/repo/raw/<branch>/<path>
 *   - https://raw.githubusercontent.com/owner/repo/(refs/heads/)?<branch>/<path>
 *   - the same without https:// or with a trailing ?query / #fragment
 *
 * Returns null for anything that doesn't look like a file URL — including bare
 * "owner/repo" (which doesn't tell us the branch or path).
 *
 * Known limitation: branches whose name contains "/" can't be disambiguated from
 * the path. The parser greedily takes the first segment after blob/raw as the
 * branch — the 404 from the API tells the user when that's wrong.
 */
function parseGithubFileUrl(input: string): { repo: string; branch: string; path: string } | null {
  // Strip protocol, trailing ?query/#fragment, surrounding whitespace.
  const cleaned = input.trim()
    .replace(/^https?:\/\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');
  let m = cleaned.match(/^raw\.githubusercontent\.com\/([^/\s]+)\/([^/\s]+)\/(?:refs\/heads\/)?([^/\s]+)\/(.+)$/i);
  if (m) return { repo: `${m[1]}/${m[2]}`, branch: m[3]!, path: m[4]! };
  m = cleaned.match(/^github\.com\/([^/\s]+)\/([^/\s]+)\/(?:blob|raw)\/([^/\s]+)\/(.+)$/i);
  if (m) return { repo: `${m[1]}/${m[2]}`, branch: m[3]!, path: m[4]! };
  return null;
}

/**
 * Fetches a single file from GitHub (private repos via the app-level PAT) and
 * applies it as a new revision — write-as-newest so it syncs to other devices.
 * The current code is archived to History first.
 */
async function pullFromGithub(id: string, onDone?: () => void): Promise<void> {
  const src = getGithubSource(id);
  if (!src) { showToast('Set a GitHub source first'); return; }
  const parsed = parseRepo(src.repo);
  if (!parsed) { showToast('Repo must be owner/repo'); return; }
  const branch = src.branch.trim() || 'main';
  const path = src.path.trim().replace(/^\/+/, '');
  const url = `https://api.github.com/repos/${parsed.owner}/${parsed.repo}/contents/${path.split('/').map(encodeURIComponent).join('/')}?ref=${encodeURIComponent(branch)}`;
  const headers: Record<string, string> = {
    Accept: 'application/vnd.github.raw',
    'X-GitHub-Api-Version': '2022-11-28',
  };
  const pat = getGithubPat();
  if (pat) headers.Authorization = `Bearer ${pat}`;

  showToast('🐙 Pulling from GitHub…');
  try {
    const resp = await fetch(url, { headers });
    if (resp.status === 404) {
      const hasPat = !!getGithubPat();
      showToast(hasPat ? 'GitHub: not found — the file, branch, or repo may have moved' :
        'GitHub: not found — if the repo is private, add a token in ⚙️ Settings → GitHub');
      return;
    }
    if (resp.status === 401 || resp.status === 403) {
      showToast('GitHub: token rejected — check it in ⚙️ Settings → GitHub');
      return;
    }
    if (!resp.ok) { showToast(`GitHub: ${resp.status} ${resp.statusText}`); return; }
    const html = await resp.text();
    if (!html.trim()) { showToast('GitHub returned an empty file'); return; }
    const rec = storage.getModule(id) as ModuleRecord | undefined;
    if (!rec) return;
    if (rec.htmlSource === html) { showToast('✓ Already up to date'); return; }
    const files: Record<string, string> = { ...rec.files, 'index.html': html };
    if (rec.htmlSource) {
      const isPlaceholder = rec.htmlSource.length < 200 && rec.htmlSource.includes('<p>');
      if (!isPlaceholder) files[`revisions/index_${rec.lastModifiedMs || Date.now()}.html`] = rec.htmlSource;
    }
    storage.putModule(id, { ...rec, htmlSource: html, files, lastModifiedMs: Date.now() });
    showToast('🐙 Pulled latest from GitHub');
    onDone?.();
    render();
  } catch (e) {
    showToast(`Pull failed: ${(e as Error).message}`);
  }
}

// ─── Create-module overlay ───────────────────────────────────────────────────

function showCreateModule(prefill?: { html?: string; name?: string; files?: Record<string, string> }): void {
  const overlay = openOverlay('Create Module');
  const body = overlay.querySelector('.overlay-body')!;
  const pName = prefill?.name ?? '';
  const pId = pName ? uniqueModuleId(pName) : '';
  body.innerHTML = `
    <section class="settings-section">
      <h3>New Module</h3>
      <p class="ss-hint">Paste an HTML module's source below. Use the Prompt Generator to draft one
        with an AI, then paste the result here. The module will sync to other devices automatically.</p>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px">
        <input id="cm-id"   class="ss-input" placeholder="module id (e.g. mynotes)" value="${escapeHtml(pId)}" />
        <input id="cm-name" class="ss-input" placeholder="display name" value="${escapeHtml(pName)}" />
      </div>
      <textarea id="cm-html" class="ss-input"
        placeholder="Paste HTML here. Must contain <!DOCTYPE html>…"
        style="min-height:220px;font-family:monospace;font-size:12px;resize:vertical;margin-bottom:8px">${escapeHtml(prefill?.html ?? '')}</textarea>
      <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:#aaa;margin-bottom:10px">
        <input type="checkbox" id="cm-sync" checked /> Sync this module (☁️ code + data)
      </label>
      <div class="ss-actions">
        <button id="cm-create-btn">➕ Create Module</button>
      </div>
    </section>
  `;
  document.getElementById('cm-create-btn')!.addEventListener('click', () => {
    const id   = (document.getElementById('cm-id')   as HTMLInputElement).value.trim().toLowerCase();
    const name = (document.getElementById('cm-name') as HTMLInputElement).value.trim();
    const html = (document.getElementById('cm-html') as HTMLTextAreaElement).value;
    const sync = (document.getElementById('cm-sync') as HTMLInputElement).checked;
    if (!/^[a-z0-9_-]{1,40}$/.test(id)) { showToast('Invalid id — use a-z, 0-9, _ or -'); return; }
    if (!name) { showToast('Name is required'); return; }
    if (!html.trim()) { showToast('Paste some HTML first'); return; }
    if (id.startsWith('__')) { showToast('IDs starting with __ are reserved'); return; }
    if (storage.getModule(id)) { showToast(`Module "${id}" already exists`); return; }
    // Preserve any extra files from an uploaded multi-file module; always
    // refresh index.html + ensure a module.json carries the label/icon to
    // Android via code sync.
    const files: Record<string, string> = { ...(prefill?.files ?? {}), 'index.html': html };
    if (!files['module.json']) {
      files['module.json'] = JSON.stringify({ label: name, icon: '📦', section: 'General' });
    }
    storage.putModule(id, {
      id, name, htmlSource: html,
      files,
      lastModifiedMs: Date.now(),
      lastDataSyncMs: 0,
      lastCodeSyncMs: 0,
      dataSyncMode: sync ? 'sync' : 'local_only',
      codeSyncMode: sync ? 'sync' : 'local_only',
    });
    overlay.dispatchEvent(new CustomEvent('overlay-close'));
    overlay.remove();
    render();
    showToast(`✓ Module "${name}" created`);
  });
}

// ─── Overlay primitive ───────────────────────────────────────────────────────

function openOverlay(title: string): HTMLDivElement {
  const root = document.createElement('div');
  root.className = 'overlay';
  root.innerHTML = `
    <div class="overlay-card">
      <div class="overlay-head">
        <h2>${escapeHtml(title)}</h2>
        <button class="overlay-close" aria-label="Close">✕</button>
      </div>
      <div class="overlay-body"></div>
    </div>
  `;
  const close = (): void => {
    root.dispatchEvent(new CustomEvent('overlay-close'));
    root.remove();
  };
  root.querySelector('.overlay-close')!.addEventListener('click', close);
  root.addEventListener('click', (e) => { if (e.target === root) close(); });
  document.body.appendChild(root);
  return root;
}

// ─── Tile interactions ──────────────────────────────────────────────────────

function openModule(id: string): void {
  // Desktop: open in the split pane, keep the sidebar. Mobile: navigate
  // full-page like Android.
  if (isDesktop()) {
    setOpenModule(id);
    return;
  }
  const url = new URL('module.html', window.location.href);
  url.searchParams.set('id', id);
  window.location.href = url.toString();
}

function attachLongPress(el: HTMLElement, handler: () => void): void {
  let timer: number | null = null;
  let fired = false;
  const start = (): void => {
    fired = false;
    timer = window.setTimeout(() => { fired = true; handler(); }, 500);
  };
  const cancel = (): void => { if (timer) { clearTimeout(timer); timer = null; } };
  el.addEventListener('mousedown', start);
  el.addEventListener('touchstart', start, { passive: true });
  el.addEventListener('mouseup', cancel);
  el.addEventListener('mouseleave', cancel);
  el.addEventListener('touchend', cancel);
  el.addEventListener('touchcancel', cancel);
  // Capture phase so the suppressor runs before the tile's own click
  // handler — otherwise a long-press would also open the module.
  el.addEventListener('click', (e) => { if (fired) { e.stopImmediatePropagation(); e.preventDefault(); } }, { capture: true });
  el.addEventListener('contextmenu', (e) => { e.preventDefault(); handler(); });
}

/** iOS (incl. iPadOS, which reports as MacIntel with a touch screen). */
function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Already launched as an installed PWA (iOS standalone or display-mode). */
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}

bootstrap().catch((err) => {
  console.error('[launcher] bootstrap failed', err);
  document.body.innerHTML = `<pre style="color:red;padding:16px">${escapeHtml(String(err))}</pre>`;
});
